import { Catalog } from './types';

/**
 * English catalog. Mirrors `catalog.es.ts` key for key — the pair is asserted
 * in `catalog.test.ts`, so adding a Spanish string without its English twin
 * fails the suite rather than silently shipping Spanish to English users.
 */
export const en: Catalog = {
  'label.alreadyReserved':
    'Sorry, the workstation you are trying to book has just been taken by someone else. Please pick another workstation or another date.',
  'label.duplicateReservationWarning':
    'You already have a reservation for this day. To book a different space, cancel your current reservation first.',
  'label.fullCapacity':
    'Sorry, this day has already reached its maximum capacity. Please try another day.',
  'label.reservationSuccessMessage':
    'Your reservation was created successfully. You can find the details on the "My reservations" page, under "Upcoming reservations".',
  'label.reservationErrorMessage':
    'Sorry, something went wrong and we could not save your reservation. Please try again.',
  'label.lightOccupation': 'Steady occupancy',
  'label.mediumOccupation': 'Approaching maximum occupancy',
  'label.maxOccupation': 'Maximum occupancy reached',
  'label.floorOccupationChangedSuccess': 'The allowed capacity was updated successfully.',
  'label.floorOccupationChangedError':
    'Sorry, we could not change the allowed capacity. Please try again.',
  'label.cancelReservationMessageFailed':
    'Sorry, something went wrong and we could not cancel your reservation. Please try again.',
  'label.cancelReservationMessageSuccess': 'Reservation cancelled successfully.',
  'label.changeWorkstationMessageSuccess': 'Workstation changed successfully.',
  'label.changeWorkstationMessageFailed':
    'Sorry, we could not change the workstation. It may already be taken — please try another one.',
  'label.editLunchSuccess': 'Lunch updated successfully.',
  'label.editLunchFailed': 'We could not update the lunch. Please try again.',
  'label.createUserMessageSuccess': 'Registration completed successfully.',
  'label.createUserMessageFailed': 'Registration could not be completed. Please try again.',
  'label.checkInMessageFailed':
    'Sorry, something went wrong and we could not check you in. Please try again.',
  'label.checkInMessageSuccess': 'Check-in completed successfully.',
  'label.checkOutMessageFailed':
    'Sorry, something went wrong and we could not check you out. Please try again.',
  'label.checkOutMessageSuccess': 'Check-out completed successfully.',
  'label.sameDayCutoff': 'Same-day reservations are not available after 4:00 p.m.',
  'label.maxBookingDateHoverMessage': 'You can only book up to',
  'label.bookingWindowPolicyHint':
    'You can book this week and next week. Another week opens every Monday at 12:00 a.m. (Costa Rica time).',
  'label.footerNeedHelp': 'Need help?',
  'label.footerCopyright': 'WPP Enterprise Solutions Costa Rica',
  'label.lunchQrTitle': 'Your lunch QR code',
  'label.lunchQrInstructions': 'Show this code when you pick up your lunch',
  'label.lunchQrNameLabel': 'Lunch for',
  'label.lunchDelivered': 'Lunch already picked up',
  'label.lunchPending': 'Pending pick-up',
  'label.lunchNoReservation': 'Book your workstation to get your lunch code',
  'label.lunchNotRequested': 'You did not request lunch for today',
  'label.lunchSurveyCta': 'How was your lunch?',
  'label.lunchSurveyCtaSubtitle': 'Your feedback helps us improve the service',
  'label.lunchSurveyButton': 'Rate lunch',
  'label.lunchSurveyTitle': 'Rate your lunch',
  'label.lunchSurveySubtitle': "Pick 1 to 5 stars based on today's experience",
  'label.lunchSurveyCommentLabel': 'Comments (optional)',
  'label.lunchSurveyCommentLabelRequired': 'Comments (required)',
  'label.lunchSurveyCommentRequiredHint':
    'Tell us what we can improve to complete your rating.',
  'label.lunchSurveyCommentPlaceholder': "Tell us what you thought of today's lunch...",
  'label.lunchSurveySubmit': 'Submit rating',
  'label.lunchSurveyCancel': 'Not now',
  'label.lunchSurveyThanks': 'Thanks for your feedback!',
  'label.lunchSurveyDone': 'You already rated your lunch today',
  'label.lunchSurveyError': 'We could not submit your rating. Please try again.',

  // --- Language selector -------------------------------------------------
  'language.label': 'Language',
  'language.aria': 'Change language',
  'language.description': 'Choose the language of the app. It is saved to your account.',
  'language.saveError': 'We could not save your language. It was applied on this device.',
  'language.lockedHint': 'This section is only available in Spanish: PROCOMER does not accept records in any other language.',

  // --- Header ------------------------------------------------------------
  'header.openMenu': 'Open menu',
  'header.closeMenu': 'Close menu',
  'header.logout': 'Log out',
  'header.myProfileTitle': 'View my profile',
  'header.viewProfileOf': "View {name}'s profile",
  'header.app.deskbooking': 'Deskbooking',
  'header.app.assets': 'Asset Control',
  'header.app.reports': 'Reports',
  'header.app.admin': 'Administration',
  'header.app.profile': 'My Profile',
  'header.app.skills': 'Skill Matrix',

  // --- Navigation --------------------------------------------------------
  'nav.deskbooking': 'Deskbooking',
  'nav.hint.newReservation': 'Pick a desk on the office map',
  'nav.hint.myReservations': 'Check in, review or cancel',
  'nav.newReservation': 'New reservation',
  'nav.myReservations': 'My reservations',
  'nav.adminReservation': 'Administrative booking',
  'nav.admin': 'Admin',
  'nav.assets': 'Asset Control',
  'nav.reports': 'Reports',
  'nav.skills': 'Skill Matrix',
  'nav.logout': 'Log out',

  // --- Profile page ------------------------------------------------------
  'profile.loadError': 'We could not load your profile: {message}',
  'profile.upcoming.one': '{count} upcoming reservation',
  'profile.upcoming.other': '{count} upcoming reservations',
  'profile.assets.one': '{count} asset in my name',
  'profile.assets.other': '{count} assets in my name',
  'profile.tab.activity': 'Activity',
  'profile.tab.skills': 'My professional profile',
  'profile.card.attendance': 'Attendance',
  'profile.card.week': 'Week',
  'profile.card.month': 'Month',
  'profile.card.year': 'Year',
  'profile.card.attendanceDesc': 'Days with an active reservation',
  'profile.card.topWeekday': 'Most frequent day',
  'profile.card.topWeekdayVisits.one': '{count} visit in the last 12 months',
  'profile.card.topWeekdayVisits.other': '{count} visits in the last 12 months',
  'profile.card.topWeekdayEmpty': 'Not enough visits yet',
  'profile.card.lateCancels': 'Late cancellations',
  'profile.card.lateCancelsDesc': '{total} in total · {month} this month',
  'profile.card.myAssets': 'Assets in my name',
  'profile.card.myAssetsDesc': 'Equipment under your responsibility',
  'profile.assetsDisclaimer':
    'The inventory is still being loaded over the next month, so the total may be incomplete.',
  'profile.panel.monthly': 'Monthly attendance',
  'profile.panel.monthlyDesc': 'Days with an active reservation in the last 12 months',
  'profile.panel.daysThisYear': '{count} days this year',
  'profile.chartTooltip':
    '{label}: {attended} days attended · {lunches} lunches · {lateCancels} late cancellations',
  'profile.preferences.title': 'Preferences',
  'profile.preferences.desc':
    'Settings saved to your account that follow you to any device',
  'profile.preferences.languageHint':
    "We detected your computer's language the first time. You can change it whenever you want.",
  // --- Profile page: charts, tables, empty states ------------------------
  'profile.chart.previousMonths': 'Previous months',
  'profile.chart.currentMonth': 'Current month',
  'profile.panel.lateLunch': 'Late lunch cancellations',
  'profile.panel.lateLunchDesc': 'Lunches cancelled after 2:30 p.m. the day before',
  'profile.panel.totalCount': '{count} in total',
  'profile.lateCancels.emptyTitle': 'No late cancellations',
  'profile.lateCancels.emptyDesc': 'Thanks for letting us know in time when you are not having lunch.',
  'profile.table.lunchDate': 'Lunch date',
  'profile.table.cancelledOn': 'Cancelled on',
  'profile.table.status': 'Status',
  'profile.table.lateStatus': 'Past the deadline',
  'profile.pagination.previous': 'Previous',
  'profile.pagination.next': 'Next',
  'profile.pagination.range': '{from}–{to} of {total}',
  'profile.panel.assets': 'Assets in my name',
  'profile.panel.assetsDesc': 'Equipment and furniture assigned to you',
  'profile.panel.assetsBadge': '{count} assets',
  'profile.assets.emptyTitle': 'No assets assigned',
  'profile.assets.emptyDesc':
    'If you have office equipment under your care, contact the person in charge of asset control.',
  'profile.table.plate': 'Tag number',
  'profile.table.category': 'Category',
  'profile.table.brandModel': 'Brand / Model',
  'profile.table.serial': 'Serial number',
  'profile.table.location': 'Location',
  'profile.asset.myHome': 'My home',
  // --- Map / booking page --------------------------------------------------
  'map.title.new': 'New reservation',
  'map.title.change': 'Change workstation',
  'map.subtitle.new': 'Pick a date and a workstation to book',
  'map.subtitle.change': 'Pick a new workstation for your reservation',
  'map.exitEditMode': 'Exit edit mode',
  // --- Booking dialog & workstation map ------------------------------------
  'common.yes': 'yes',
  'common.no': 'no',
  'common.yesCap': 'Yes',
  'common.noCap': 'No',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.accept': 'Got it',
  'extraLunch.deleteConfirm': 'Delete the extra lunch assigned to {name}?',
  'common.date': 'Date',
  'common.link': 'link',
  'booking.detailTitle': 'Reservation details',
  'booking.stationLine': 'Workstation: {station}',
  'booking.dateLine': 'Date: {date}',
  'booking.parkingQuestion': 'Do you need a parking space?',
  'booking.parkingPolicyBefore': 'Parking spaces are limited, please read our ',
  'booking.parkingPolicyLink': 'Parking Policy',
  'booking.parkingPolicyAfter': '. If you use the basement parking, please choose "No"',
  'booking.lunchTitle': 'Lunch',
  'booking.lunchUnclaimed': 'Not picked up',
  'noShowLunch.title': 'Your lunch was cancelled at 12pm',
  'noShowLunch.body': 'We did not record your entry to the office before noon. If you are already here, you can get it back.',
  'noShowLunch.cta': 'Reactivate my lunch',
  'noShowLunch.success': 'Done! Your lunch is active again.',
  'noShowLunch.error': 'We could not reactivate your lunch. Please try again.',
  'noShowLunch.blocked.PICKUP_CLOSED': 'Lunch service is already closed (2:00 pm).',
  'noShowLunch.blocked.LUNCH_REASSIGNED': 'Your lunch was already given to someone else.',
  'noShowLunch.blocked.NO_LUNCH_LEFT': 'There are no lunches left for today.',
  'noShowLunch.blocked.DESK_TAKEN': 'Someone else booked your desk. Book another desk for today and come back here to get your lunch.',
  'noShowLunch.blocked.ALREADY_HAS_LUNCH': 'Your booking for today already includes lunch.',
  'lunchQr.fullScreen': 'Show full screen',
  'lunchQr.fullScreenHint': 'Turn your screen brightness up and hold the code to the camera.',
  'lunchQr.close': 'Close',
  'booking.lunchChecking': 'Checking lunch availability...',
  'booking.lunchNoFriday': 'There is no lunch service on Fridays.',
  'booking.lunchDayDisabled': 'There is no lunch service on this day.',
  'booking.lunchCutoff': 'Booking closed: the deadline is 2:30 PM the previous {day}.',
  'booking.lunchCutoffFriday': 'Friday',
  'booking.lunchCutoffDay': 'day',
  'booking.lunchQuestion': 'Do you want lunch?',
  'booking.lunchUnavailable': '(No availability)',
  'booking.lunchTimeLabel': 'Lunch time',
  'booking.slotUnavailable': ' (Not available)',
  'booking.dietaryPreferences': 'Dietary preferences',
  'booking.vegetarian': 'Vegetarian',
  'booking.lunchPolicyBefore':
    'To make sure there is lunch for everyone, booking it through Deskbooking is required. It must be done before 2:30 PM the previous day. Check the guidelines at this ',
  'booking.changeStationTitle': 'Change workstation',
  'booking.changeStationQuestion':
    'You already have a reservation for this day. Do you want to change your workstation to',
  'booking.changeStationKeepsData': 'Your lunch and the rest of your details stay unchanged.',
  'booking.changeAction': 'Change',
  'booking.cancelTitle': 'Cancel reservation',
  'booking.cancelQuestion': 'Are you sure you want to cancel the reservation?',
  'booking.checkInQuestion': 'Are you sure you want to check in for this reservation?',
  'booking.checkOutQuestion': 'Are you sure you want to check out for this reservation?',
  'booking.howItWorks': 'How it works',
  'booking.hintHighTables': 'Rows BC and DE are high tables and have no screen or keyboard',
  'booking.hintOccupied': 'Click a taken (red) workstation to see who booked it',
  'booking.emptyTitle': 'Pick a workstation',
  'booking.emptySubtitle': 'Click an available workstation on the map to book it',
  'booking.stationReserved': 'Workstation booked:',
  'booking.stationSelected': 'Workstation selected:',
  'booking.stationAria': 'Workstation {station}',
  'booking.verifying': 'Checking...',
  'booking.currentStation': 'Current workstation',
  'booking.confirmChange': 'Confirm change',
  'booking.reserve': 'Book',
  'booking.frequent': 'Frequent',
  'booking.frequentTooltip': '{label} · {times} bookings in the last 90 days',
  'booking.frequentTakenToday': ' · taken on this day',
  // --- My reservations (today / upcoming) ----------------------------------
  'common.edit': 'Edit',
  'reservations.todayTitle': 'Today\'s reservation',
  'reservations.todayEmpty': 'You have no reservation for today.',
  'reservations.upcomingTitle': 'Upcoming reservations',
  'reservations.upcomingEmpty': 'You have no upcoming reservations.',
  'reservations.colStation': 'Workstation / Room',
  'reservations.colDate': 'Date',
  'reservations.colNeedsLunch': 'Lunch requested',
  'reservations.colLunchTime': 'Lunch time',
  'reservations.colLunchTimeMobile': 'Lunch time',
  'reservations.stationPrefix': 'Workstation',
  'reservations.autoCheckIn': 'Automatic check-in',
  'reservations.autoCheckOut': 'Automatic check-out',
  'reservations.lunchDeliveredAt': 'Your lunch was already picked up',
  'reservations.lunchDeliveredAtTime': ' at {time}',
  'reservations.lunchDeliveredNoQr': '. There is no need to show the QR code.',
  'reservations.lunchScheduleLine': 'Time slot: {time}',
  'reservations.lunchTimeJoin': ' to ',
  'reservations.extraLunchAvailable.one':
    'There is {count} lunch available for this day from late cancellations. If you need one, contact the HR team to have it assigned to you.',
  'reservations.extraLunchAvailable.other':
    'There are {count} lunches available for this day from late cancellations. If you need one, contact the HR team to have them assigned to you.',
  // --- Reservation row actions ---------------------------------------------
  'reservations.editSeat': 'Edit seat',
  'reservations.editLunch': 'Edit lunch',
  // --- Reservations page, check-in, lunch edit, filters --------------------
  'common.save': 'Save',
  'common.success': 'Done',
  'common.error': 'Error',
  'myReservations.title': 'My Reservations',
  'myReservations.subtitle': 'Check your current reservations and history',
  'checkIn.detailTitle': 'Check-in details',
  'checkIn.stationLabel': 'Workstation:',
  'checkIn.dateLabel': 'Date:',
  'occupation.title': 'Occupancy',
  'occupation.current': 'Current occupancy:',
  'occupation.max': 'Maximum expected occupancy:',
  'editLunch.title': 'Edit lunch',
  'editLunch.dayDisabled': 'There is no lunch service on {date}.',
  'editLunch.cutoffPassed':
    'The deadline to change lunch (2:30 PM the previous day) has passed.',
  'editLunch.lateChangeWarning':
    'The deadline (2:30 PM the previous day) has passed. This change will be recorded as a late modification.',
  'editLunch.vegan': 'Vegan',
  'filter.selectPrefix': 'Choose a',
  'filter.workstation': 'workstation',
  'filter.meetingRoom': 'meeting room',
  'filter.workstationCard': 'Workstation',
  'workstations.stationLabel': 'Workstation {station}',
  'workstations.reservationCount.one': '{count} reservation',
  'workstations.reservationCount.other': '{count} reservations',
  'events.type.celebration': 'Celebration',
  'events.type.general': 'General',
  // --- Event banner types --------------------------------------------------
  'events.type.connect': 'Connect / All Hands',
  // --- Reservation history & QR shortcut pages -----------------------------
  'common.exit': 'Close',
  'common.information': 'Information',
  'history.empty': 'You have no reservations this month.',
  'shortcut.notYours': 'This reservation does not belong to this user',
  'shortcut.tooEarly': 'It is not yet the date when you can check in',
  'shortcut.alreadyCheckedIn': 'This reservation has already been checked in',
  'shortcut.notFound': 'That reservation does not exist',
  'shortcut.checkInUnavailable': 'Manual check-in is no longer available from this link.',
  'shortcut.cancelFailed': 'The reservation could not be cancelled.',
  'shortcut.loadFailed': 'The reservation details could not be loaded.',
  'shortcut.notFoundDot': 'That reservation does not exist.',
  'shortcut.notYoursDot': 'This reservation does not belong to this user.',
  'shortcut.alreadyProcessed': 'This reservation has already been processed.',
  'shortcut.signInRequired': 'Please sign in to open this page.',
  // --- Map legend, popover and sidebar -------------------------------------
  'map.legendTitle': 'Legend',
  'map.legend.available': 'Available',
  'map.legend.occupied': 'Taken',
  'map.legend.unavailable': 'Unavailable',
  'map.legend.selected': 'Selected',
  'map.imageAlt': 'Building map',
  'map.favoriteTooltip': '{station} · one of your frequent workstations',
  'map.occupiedBy': 'Taken by',
  'map.external': 'External',
  'map.allDay': 'All day',
  'map.hourAria': 'time',
  'map.occupancyTitle': 'Overall occupancy',
  'map.spaceOccupancy': 'Space occupancy',
  'common.selectOption': 'Choose an option',
  // --- Floor plan labels (drawn inside the map) ----------------------------
  'map.floorPlan.hallway': 'Hallway',
  'map.floorPlan.mainHallwayLine1': 'Main',
  'map.floorPlan.mainHallwayLine2': 'Hallway',
  'map.floorPlan.emergencyExitLine1': 'Emergency',
  'map.floorPlan.emergencyExitLine2': 'Exit',
  // --- Login, talent gate and the what's-new modal ------------------
  'common.understood': 'Got it',
  'common.selectDate': 'Pick a date',
  'login.retrying': 'Something went wrong signing you in. Retrying automatically...',
  'login.failed': 'We could not sign you in. Please try again.',
  'talent.soon.badge': 'Coming soon',
  'talent.soon.text':
    'We are putting the finishing touches on the Skill Matrix. Soon you will be able to fill in your technical profile, keep your certifications up to date and see what your team knows how to do.',
  'talent.soon.note':
    'We will let you know the moment it opens. You do not have to do anything yet.',
  'talent.noProfile':
    'You do not have a technical profile yet. If you think this is a mistake, write to an administrator.',
  'whatsNew.title': 'What\'s new in Deskbooking',
  'whatsNew.subtitle': 'Here is what we added this month',
  'whatsNew.comingSoon': 'In progress',
  'whatsNew.profile.title': 'Your profile',
  'whatsNew.profile.desc':
    'Open it from your name in the top bar: it shows your attendance by week, month and year, your most frequent day at the office and your upcoming reservations.',
  'whatsNew.brand.title': 'New WPP identity',
  'whatsNew.brand.desc':
    'Deskbooking moved to the WPP Enterprise Solutions brand: new typeface, colours and logo.',
  'whatsNew.mobile.title': 'Mobile improvements',
  'whatsNew.mobile.desc':
    'Drop-down menu, automatically centred map, and reservation lists redesigned for small screens.',
  'whatsNew.lunchHistory.title': 'Lunch history',
  'whatsNew.lunchHistory.desc':
    'Check your late lunch cancellations from your profile, with the date and time of each one.',
  'whatsNew.assets.title': 'Assets in my name',
  'whatsNew.assets.desc':
    'See the equipment under your responsibility. We are still loading the inventory over the next month.',
  // --- Skill Matrix: a person's own profile --------------------------------
  'skills.since.today': 'today',
  'skills.since.yesterday': 'yesterday',
  'skills.since.days': '{count} days ago',
  'skills.since.months.one': '{count} month ago',
  'skills.since.months.other': '{count} months ago',
  'skills.since.years.one': '{count} year ago',
  'skills.since.years.other': '{count} years ago',
  'skills.profileNotFound': 'Profile not found.',
  'skills.freelance': 'Freelance',
  'skills.inactive': 'Inactive',
  'skills.field.email': 'Email',
  'skills.field.languages': 'Languages',
  'skills.field.experience': 'Years of experience',
  'skills.field.registered': 'Skills on record',
  'skills.advancedCount': '· {count} at advanced',
  'skills.noAccount': 'No Deskbooking account',
  'skills.noAccountTitle':
    'They have no Deskbooking account, so they cannot edit their own profile. An administrator keeps it up to date.',
  'skills.saving': 'Saving…',
  'skills.saved': 'Saved',
  'skills.notSaved': 'Not saved',
  'skills.hideLegend': 'Hide the explanation',
  'skills.showLegend': 'What does each level mean?',
  'skills.addSkill': '+ Add skill',
  'skills.removeFromProfile': 'Remove from my profile',
  'skills.emptyOwn':
    'Mark the level of the technologies you actually work with. Anything left at “—” stays off your profile, and you can bring it back any time from “Add skill”.',
  'skills.emptyOther': 'This person has not recorded any skills yet.',
  'skills.saveErrorTail': '— your changes are still on screen; tap a level again to retry.',
  'skills.noIssuer': 'No issuer',
  'skills.neverExpires': 'Never expires',
  'skills.expired': 'Expired',
  'skills.daysShort': '{count} d',
  'skills.project': 'Project',
  'skills.ongoing': 'Ongoing',
  'skills.level.basic': 'Basic',
  'skills.level.intermediate': 'Intermediate',
  'skills.level.advanced': 'Advanced',
  // --- Skill Matrix: team directory ----------------------------------------
  'skills.searchTeam': 'Search by name or skill…',
  'skills.clear': 'Clear',
  'skills.typeToSearch': 'Type {count} letters to search',
  'skills.peopleCount.one': '{count} person',
  'skills.peopleCount.other': '{count} people',
  'skills.noMatches': 'Nobody matches that search.',
  'skills.moreCount': '+{count} more',
  // --- Skill Matrix: editors -----------------------------------------------
  'skills.save': 'Save',
  'skills.close': 'Close',
  'skills.add': 'Add',
  'skills.required': 'Name *',
  'skills.err.nameRequired': 'The name is required.',
  'skills.err.expiryBeforeIssue': 'The expiry date cannot be earlier than the issue date.',
  'skills.err.years': 'Years of experience must be a whole number between 0 and 60.',
  'skills.err.positionRequired': 'The job title is required.',
  'skills.cert.edit': 'Edit certification',
  'skills.cert.new': 'New certification',
  'skills.cert.issuer': 'Issuer',
  'skills.cert.issued': 'Issued',
  'skills.cert.expires': 'Expires',
  'skills.cert.credential': 'Credential or URL',
  'skills.cert.relatedSkill': 'Related skill',
  'skills.cert.none': 'None',
  'skills.cert.noExpiryHint': 'Leave "Expires" empty if the certification does not expire.',
  'skills.project.edit': 'Edit project',
  'skills.project.new': 'Add project',
  'skills.project.nameLabel': 'Project *',
  'skills.project.role': 'Role on the project',
  'skills.project.start': 'Start',
  'skills.profile.edit': 'Edit profile',
  'skills.profile.seniority': 'Seniority',
  'skills.profile.undefined': 'Not set',
  'skills.profile.contract': 'Contract',
  'skills.profile.linkedAccount': 'Linked account',
  'skills.profile.active': 'Active profile',
  'skills.profile.position': 'Job title *',
  'skills.profile.removeLanguage': 'Remove language',
  'skills.profile.addLanguage': '+ Add language',
  'skills.profile.aboutMe': 'About me',
  'skills.profile.aboutMePlaceholder':
    'Visible to the whole team. What you like working on, what you can help solve, what you are learning…',
  'skills.profile.github': 'GitHub username',
  'skills.profile.githubPlaceholder': 'Optional. Your username or a link to your profile',
  'skills.profile.comments': 'Comments',
  'skills.profile.commentsPlaceholder': 'Extra context, interests, availability…',
  'skills.add.title': 'Add a skill',
  'skills.add.hint':
    'Only add technologies you have actually worked with. They come in at level {level} and you can adjust it afterwards; if you do not know it, better to leave it out.',
  'skills.add.searchLabel': 'Search',
  'skills.add.searchPlaceholder': 'Terraform, Contentful, Snowflake…',
  'skills.add.typeToSearch': 'Type to search the catalogue.',
  'skills.add.noneInCatalog':
    'Nothing by that name in the catalogue. Ask a skills administrator to add it.',
  'skills.add.notFound':
    'We could not find “{term}” in the catalogue. You can add it yourself and it becomes available to everybody.',
  'skills.add.category': 'Category',
  'skills.add.creating': 'Adding…',
  'skills.add.createAndAdd': 'Create "{term}" and add it',
  // --- Skill Matrix: first-time skills form (gates the module) -------------
  'nav.skillsPending': 'Your skills are missing',
  'skills.ob.title': 'Tell us what you work with',
  'skills.ob.intro':
    'About 16 skills, the ones for your role. It saves as you answer, so you '
    + 'can stop halfway and pick it up later.',
  'skills.ob.roleQuestion': 'Which of these roles is closest to your day to day?',
  'skills.ob.roleHelp':
    'Pick the nearest one. It decides which skills we ask you about, and an '
    + 'administrator can adjust it later if needed.',
  'skills.ob.rolesMulti': 'Pick more than one if you do two jobs.',
  'skills.ob.stepOf': 'Step {step} of {total}',
  'skills.ob.back': 'Back',
  'skills.ob.finish': 'Finish',
  'skills.ob.gateTitle': 'Before you go in',
  'skills.ob.savedNote': 'Saved as you go. You can close this and come back.',
  'skills.ob.emptyGrid':
    'Your role has no skills in its grid yet. Let a skills administrator know '
    + 'so they can set it up.',
  'skills.ob.noRoles':
    'No roles have been set up yet. Let a skills administrator know.',
  'skills.ob.reviewNote':
    'You can change any level later from your profile.',
  'skills.ob.bonusTitle': 'AI — counts as extra',
  'skills.ob.bonusIntro':
    'This one belongs to no role in particular: knowing it or not does not '
    + 'change what your job expects. It counts as extra, so it is worth having '
    + 'on record.',
  'skills.ob.continueAnyway': 'Go into the module anyway',
  'skills.ob.dontKnow': 'Leave anything you do not work with on —. That is fine.',
  'skills.dir.stale': 'Out of date',
  'skills.dir.staleCount': '{count} answered more than {months} months ago',
  'skills.dir.reAskStale': 'Ask them again',
  'skills.dir.reAskStaleConfirm':
    'Ask the {count} people who answered more than {months} months ago to fill '
    + 'in the form again? No levels are deleted: they see what they gave and confirm it.',
  'skills.dir.reAskRole': 'Ask this role again',
  'skills.dir.reAskRoleConfirm':
    'Ask everybody in {role} to fill in the form again? This is what you use '
    + 'after changing that role\'s grid. No levels are deleted.',
  'skills.dir.reAskAll': 'Ask everybody again',
  'skills.dir.reAskAllConfirm':
    'Ask the {count} people who already completed it to fill in the form again? '
    + 'This is the yearly refresh. No levels are deleted.',
  'skills.dir.reAskDone': 'Done: {count} people will see the form again.',
  'skills.ob.roleMismatch': 'None of these really describes what I do',
  'skills.ob.roleMismatchHelp':
    'Pick the closest one anyway so you can carry on. We will let an '
    + 'administrator know so the role you are missing gets created.',
  'skills.dir.mismatchHint': 'Their HR job titles suggest: {list}',
  'skills.dir.mismatch': 'Approximate role',
  'skills.dir.mismatchCount': '{count} say no role fits them',
  'skills.dir.seeMismatch': 'See who',
  'skills.dir.seeMismatchOff': '← Show everyone',
  'skills.dir.mismatchTitle':
    'They picked the nearest role, not theirs. Assigning roles by hand clears this.',
  'skills.dir.formState': 'Form',
  'skills.dir.formStateLabel': 'Filter by form state',
  'skills.dir.anyFormState': 'Any state',
  'skills.dir.onlyPending': 'Pending only',
  'skills.dir.onlyDone': 'Completed only',
  'skills.dir.done': 'Complete',
  'skills.dir.pending': 'Pending',
  'skills.dir.halfway': 'In progress',
  'skills.dir.rollout': '{done} of {total} have filled in their skills',
  'skills.dir.notStarted': '{count} have not opened it',
  'skills.dir.seePending': 'Show pending only',
  'skills.dir.seeEveryone': '← Show everyone',
  'skills.dir.resetAsk': 'Ask to update',
  'skills.dir.resetTitle':
    'Asks them for the form again. The levels they already gave are kept.',
  'skills.dir.resetConfirm':
    'Ask {name} to fill in the form again? No levels are deleted.',
  'skills.bonusScopeOn': '★ Show plus only',
  'skills.bonusScopeOff': '← Leave plus',
  'skills.bonusScopeTitle':
    'Skills asked of everybody, whatever their role. Combines with a role.',
  'skills.bonus.title': 'Plus skills (asked of everybody)',
  'skills.bonus.intro':
    'Asked of everybody whatever their role, in a section of their own in the '
    + 'form. Removing one deletes no levels already given.',
  'skills.bonus.search': 'Search for a skill to add…',
  'skills.bonus.add': 'Add',
  'skills.bonus.remove': 'Remove',
  'skills.bonus.removeTitle':
    'Stops asking everybody. Levels already given are left untouched.',
  'skills.newInGrid':
    '{count} skills were added to your role\'s grid after you filled in the '
    + 'form. Answer them here and you are done.',
  // --- Skill Matrix: job roles (what decides each person's grid) -----------
  'skills.allRoles': 'All roles',
  'skills.anyRole': 'Any role',
  'skills.role.filterLabel': 'Filter by role',
  'skills.col.missing': 'Not covered',
  'skills.col.missingOf': 'Out of {expected} people in scope',
  'skills.gap.title': 'Skills the role asks for that nobody has declared',
  'skills.gap.tag': 'nobody',
  'skills.gap.count': '○ {count} not covered',
  'skills.gap.seeAll': '← See all',
  'skills.profile.roles': 'Roles',
  'skills.profile.rolesHelp':
    'They decide which skills are asked: the grid is the union of the roles '
    + 'ticked. With none, the whole catalogue is shown.',
  'skills.profile.role': 'Role',
  'skills.profile.roleHelp':
    'Decides which skills they are asked about. With no role the whole '
    + 'catalogue is shown.',
  'skills.profile.noRole': 'No role',
  'skills.tab.roles': 'Roles & grid',
  'skills.roles.title': 'Roles and their skill grid',
  'skills.roles.intro':
    'Each role decides which skills the people holding it are asked about. A '
    + 'QA should not be shown the back-end grid: it adds nothing and it '
    + 'muddies the coverage report.',
  'skills.roles.newRole': 'New role',
  'skills.roles.name': 'Name',
  'skills.roles.nameEn': 'English name',
  'skills.roles.nameEnHelp': 'Optional. Left empty, the Spanish name is used.',
  'skills.roles.description': 'Description',
  'skills.roles.descriptionEn': 'English description',
  'skills.roles.active': 'Active',
  'skills.roles.skillCount': '{count} skills in the grid',
  'skills.roles.editGrid': 'Edit grid',
  'skills.roles.gridFor': '{role} grid',
  'skills.roles.gridNote':
    'Removing a skill from a role does not delete anybody\'s level: it stays '
    + 'on their profile as an added skill.',
  'skills.roles.searchSkills': 'Search skill…',
  'skills.roles.wholeCategory': 'Whole category',
  'skills.roles.noneOfCategory': 'None',
  'skills.roles.selected': '{count} selected',
  'skills.roles.empty': 'No roles created yet.',
  'skills.roles.nameRequired': 'Enter a name of at least 2 characters.',
  'skills.roles.inactive': 'Inactive',
  // --- Skill Matrix: coverage report, charts and directory -----------------
  'skills.allCategories': 'All categories',
  'skills.risk.title': 'Skills that depend on a single person',
  'skills.risk.seeAll': '← See all',
  'skills.risk.count': '⚠ {count} at risk',
  'skills.risk.onePerson': '1 person',
  'skills.risk.onePersonTitle': 'Only one person declares this skill',
  'skills.expiringTitle': 'Certifications about to expire',
  'skills.daysCount': '{count} days',
  'skills.col.skill': 'Skill',
  'skills.col.category': 'Category',
  'skills.col.distribution': 'Distribution',
  'skills.col.basicShort': 'Basic',
  'skills.col.interShort': 'Inter.',
  'skills.col.advShort': 'Adv.',
  'skills.col.total': 'Total',
  'skills.seeCharts': 'See charts',
  'skills.chart.distribution': 'Distribution',
  'skills.chart.category': 'Category',
  'skills.chart.declare.one': 'person declares this skill.',
  'skills.chart.declare.other': 'people declare this skill.',
  'skills.chart.blindSpot': ' Nobody at advanced level — this could be a blind spot.',
  'skills.chart.shapeOf': 'Shape of the {category} category.',
  'skills.chart.showingTop': ' Showing the {shown} with the most people out of {total}.',
  'skills.dir.search': 'Search by name, job title or email…',
  'skills.dir.anySkill': 'Any skill',
  'skills.dir.level1': 'Level 1+ (basic)',
  'skills.dir.level2': 'Level 2+ (intermediate)',
  'skills.dir.level3': 'Level 3 (advanced)',
  'skills.dir.anySeniority': 'Any seniority',
  'skills.dir.bothContracts': 'FTE and freelance',
  'skills.dir.onlyFte': 'FTE only',
  'skills.dir.onlyFreelance': 'Freelance only',
  'skills.dir.person': 'Person',
  'skills.dir.level': 'Level',
  'skills.dir.certs': 'Certs',
  'skills.dir.contract': 'Contract',
  'skills.dir.noResults': 'No results with those filters.',
  // --- Skill Matrix: two the accent grep could not see ---------------------
  'skills.err.projectRequired': 'The project is required.',
  'skills.experienceWith': 'Experience with',
  // --- Reports: shell, presets and comparison metrics ----------------------
  'reports.title': 'Reports',
  'reports.forbidden.title': 'Access restricted',
  'reports.forbidden.body':
    'You do not have permission to view Reports. If you think this is a mistake, contact an administrator.',
  'reports.preset.all': 'All time',
  'reports.preset.last9': 'Last 9 days',
  'reports.preset.last30': 'Last 30 days',
  'reports.preset.last90': 'Last 3 months',
  'reports.preset.thisMonth': 'This month',
  'reports.subtitle.assets': 'Asset inventory summary and CSV download.',
  'reports.subtitle.fullHistoryRange': 'All time · from {from} to {to}',
  'reports.subtitle.range': 'From {from} to {to}',
  'reports.subtitle.fullHistory': 'All time',
  'reports.subtitle.noData': 'No data in the selected range',
  'reports.previousDay': ' · previous day',
  'reports.page.previous': 'Previous page',
  'reports.page.next': 'Next page',
  // Visit report spreadsheet (Admin dashboard and Reservations page)
  'export.sheetName': 'Report',
  'export.visits.name': 'Employee full name',
  'export.visits.phone': 'Phone number',
  'export.visits.date': 'Office visit date',
  'export.visits.checkin': 'Did they check in for the reservation?',
  'export.visits.lunchTime': 'Lunch time',
  'reports.exportCsv': 'Export CSV',
  'reports.compareMonths': 'Compare months',
  'reports.compareGlobal': 'Overall comparison for the current tab',
  'reports.metric.late': 'Late cancellations',
  'reports.metric.requested': 'Lunches requested',
  'reports.metric.ceded': 'Lunches passed on',
  'reports.metric.responses': 'Survey responses',
  'reports.metric.rating': 'Average rating',
  'reports.metric.reservations': 'Reservations this month',
  'reports.metric.attendees': 'People with reservations',
  'reports.loadError': 'The report could not be loaded',
  'reports.tryLater': 'Please try again later.',
  // --- Reports: the "in <period>" phrase appended to sentences -------------
  'reports.period.between': 'from {from} to {to}',
  'reports.period.since': 'since {from}',
  'reports.period.until': 'up to {to}',
  'reports.period.custom': 'in the custom period',
  'reports.period.allTime': 'across all time',
  'reports.period.selected': 'in the selected period',
  'reports.period.inPreset': 'in the {preset}',
  // --- Reports: lunch tab --------------------------------------------------
  'reports.from': 'From',
  'reports.to': 'To',
  'reports.exportTitle': 'Download all the data in the current report',
  'reports.clearSearch': 'Clear search',
  'reports.legend.q': 'What counts as a late cancellation?',
  'reports.legend.a':
    'A lunch cancelled after the cut-off (2:30 p.m. the previous working day), once the order has gone to the supplier and the food cannot be recovered. This report shows late cancellations only.',
  'reports.legend.importantLabel': ' Important:',
  'reports.legend.startDate': 'lunch reports start from 20 April 2026.',
  'reports.card.lunchReservations': 'Reservations with lunch',
  'reports.card.lunchReservationsDesc': 'Lunches booked {period}.',
  'reports.card.lateCancels': 'Late cancellations',
  'reports.card.lateCancelsDesc': '{count} late {period}.',
  'reports.card.unclaimed': 'Lunches not picked up',
  'reports.card.unclaimedDesc': 'Not picked up {period} (no QR and no manual check-off).',
  'reports.card.lateRate': '% of late cancellations',
  'reports.card.lateRateDesc': '≈ {rate} out of every 100 reservations {period}.',
  'reports.byMonth.title': 'Late cancellations by month',
  'reports.byMonth.desc': 'See how they change month to month',
  'reports.col.month': 'Month',
  'reports.col.late': 'Late',
  'reports.col.comparison': 'Comparison',
  'reports.col.date': 'Date',
  'reports.col.day': 'Day',
  'reports.byDow.title': 'Late cancellations by day of the week',
  'reports.byDow.desc': '{count} late {period}. There is no lunch on Fridays',
  'reports.lateSuffix': 'late',
  'reports.topDays.title': 'Days with the most incidents',
  'reports.topDays.desc': 'The 10 dates with the most late cancellations',
  'reports.perPerson.title': 'Impact per person: late and not picked up',
  'reports.perPerson.desc':
    'Includes late cancellations and lunches that were booked but never picked up (no QR and no manual check-off on the tablet). Search by name or email.',
  // --- Reports: per-person detail and lunch orders -------------------------
  'reports.col.person': 'Person',
  'reports.col.unclaimed': 'Not picked up',
  'reports.col.detail': 'Detail',
  'reports.sort.descending': ' descending',
  'reports.sort.ascending': ' ascending',
  'reports.sort.byLate': 'Sort by late cancellations{direction}',
  'reports.sort.byUnclaimed': 'Sort by not picked up{direction}',
  'reports.noPeople': 'No people found',
  'reports.noPeopleFor': 'No people found for "{term}"',
  'reports.loadingDetail': 'Loading detail…',
  'reports.noLateForPerson': 'No late cancellations for this person.',
  'reports.noUnclaimedForPerson': 'No unclaimed lunches for this person.',
  'reports.detail.reservation': 'Booked: {date}',
  'reports.detail.cancelled': 'Cancelled: {time}',
  'reports.ceded.title': 'Lunches passed on and lost, by month',
  'reports.ceded.desc':
    'Based on late cancellations. Passed on: leftovers reassigned. Lost: leftovers nobody could take as an extra',
  'reports.col.lateBase': 'Late (base)',
  'reports.col.ceded': 'Passed on',
  'reports.col.notCeded': 'Lost',
  // --- Reports: lunch satisfaction survey ----------------------------------
  'reports.survey.title': 'Survey results',
  'reports.survey.desc':
    'Satisfaction rating (1 to 5 stars) people leave after receiving their lunch',
  'reports.survey.emptyTitle': 'No responses yet',
  'reports.survey.emptyDesc': 'No lunch surveys have been recorded in the selected range yet.',
  'reports.survey.average': 'Average rating',
  'reports.survey.responseRate': '% who responded',
  'reports.survey.responseRateDesc': 'of {count} lunches delivered {period}.',
  'reports.survey.distribution': 'Rating distribution',
  'reports.survey.distributionDesc': 'How many people gave each rating',
  'reports.survey.byMonth': 'Satisfaction by month',
  'reports.survey.byMonthDesc': 'Average stars and number of responses per month',
  'reports.col.average': 'Average',
  'reports.col.responses': 'Responses',
  'reports.survey.comments': 'Recent comments',
  'reports.survey.commentsDesc': 'Feedback people left when rating their lunch',
  // --- Reports: deskbooking tab --------------------------------------------
  'reports.desk.legendLabel': 'Reservations',
  'reports.desk.legend':
    ': workstations booked in the office. Each reservation counts as one recorded visit. These figures come from active reservations (confirmed, checked in or checked out), not from verified physical presence.',
  'reports.desk.reservations': 'Reservations',
  'reports.desk.reservationsDesc': 'Workstations booked {period}.',
  'reports.desk.dailyAverage': 'Daily average of reservations',
  'reports.desk.dailyAverageDesc': 'reservations per day on average {period}.',
  'reports.desk.peakDay': 'Busiest day',
  'reports.desk.peakDayDesc': '{count} reservations ({dow}) {period}.',
  'reports.desk.topDays': 'Busiest days',
  'reports.desk.topDaysDesc': 'The 10 dates with the most workstations booked',
  'reports.desk.byDow': 'Reservations by day of the week',
  'reports.desk.byDowDesc': 'Find out which days the office fills up most',
  'reports.desk.byPerson': 'Reservations per person and month',
  'reports.desk.byPersonDesc':
    'Number of days each person had a reservation within a single month. Search by name or email',
  'reports.col.days': 'Days',
  'reports.desk.noAttendance': 'People with no attendance, by month',
  'reports.desk.noAttendanceDesc':
    'Active people who recorded no reservations in the selected month',
  'reports.searchPerson': 'Search for a person…',
  // --- Reports: CSV export headers and section names -----------------------
  'reports.csv.tab': 'Tab',
  'reports.csv.section': 'Section',
  'reports.csv.field': 'Field',
  'reports.csv.value': 'Value',
  'reports.csv.detail': 'Detail',
  'reports.csv.tabLunch': 'Lunches',
  'reports.csv.tabDesk': 'Deskbooking',
  'reports.csv.summary': 'Summary',
  'reports.csv.lateRatePlain': '% late cancellations',
  'reports.csv.cancelsByMonth': 'Cancellations by month',
  'reports.csv.cancelsByDay': 'Cancellations by day',
  'reports.csv.orders': 'Orders',
  'reports.csv.totalRequested': 'Total requested',
  'reports.csv.totalCeded': 'Total passed on',
  'reports.csv.ordersByMonth': 'Orders by month',
  'reports.csv.survey': 'Survey',
  'reports.csv.totalResponses': 'Total responses',
  'reports.csv.totalDelivered': 'Total delivered',
  'reports.csv.averageRating': 'Average rating',
  'reports.csv.responseRate': 'Response rate',
  'reports.csv.surveyDistribution': 'Survey distribution',
  'reports.csv.stars': '{count} stars',
  'reports.csv.surveyByMonth': 'Survey by month',
  'reports.csv.monthComparison': 'Month comparison',
  // --- Asset Control: list, filters and table ------------------------------
  'common.previousArrow': '← Previous',
  'common.nextArrow': 'Next →',
  'common.pageOf': 'Page {page} of {total}',
  'assets.list.title': 'Asset list',
  'assets.list.count': '({count} assets)',
  'assets.antiquity.all': 'Age: All',
  'assets.antiquity.lessThan1': 'Age: Under 1 year',
  'assets.antiquity.oneToThree': 'Age: 1 to 3 years',
  'assets.antiquity.threeToFive': 'Age: 3 to 5 years',
  'assets.antiquity.fiveToTen': 'Age: 5 to 10 years',
  'assets.antiquity.tenPlus': 'Age: Over 10 years',
  'assets.filter.categoryAll': 'Category: All',
  'assets.filter.category': 'Category: {name}',
  'assets.filter.locationAll': 'Location: All',
  'assets.filter.locationOffice': 'Location: Office',
  'assets.filter.locationHome': 'Location: Employee\'s home',
  'assets.filter.vatAll': 'VAT: All',
  'assets.filter.vatYes': 'VAT: Yes',
  'assets.filter.vatNo': 'VAT: No',
  'assets.filter.locationChip': 'Location:',
  'assets.filter.clearLocation': 'Clear location filter',
  'assets.loadError': 'Could not load assets: {message}',
  'assets.col.plate': 'Tag',
  'assets.col.brand': 'Brand',
  'assets.col.serialModelLine1': 'Serial number /',
  'assets.col.serialModelLine2': 'Model',
  'assets.col.category': 'Category',
  'assets.col.assignedTo': 'Assigned to',
  'assets.col.purchaseDateLine1': 'Purchase',
  'assets.col.purchaseDateLine2': 'date',
  'assets.col.vat': 'VAT',
  'assets.col.actions': 'Actions',
  'assets.col.date': 'Date',
  'assets.homeOf': '{name}\'s home',
  'assets.employee': 'employee',
  // --- Asset Control: search, detail and change log ------------------------
  'assets.noPermission':
    'You do not have permission to open Asset Control. If you think this is a mistake, contact an administrator.',
  'assets.search.placeholderShort': 'Search by name or tag number',
  'assets.search.placeholder': 'Search by tag number or person name',
  'assets.search.errorTitle': 'The search failed',
  'assets.search.errorText':
    'Something went wrong looking up the assets. Check your connection and try again.',
  'assets.search.typeMoreTitle': 'Type a little more',
  'assets.search.typeMoreText':
    'Enter at least 2 characters to search by tag number, brand, model, serial or the name of the person it is assigned to.',
  'assets.search.noResultsTitle': 'No results',
  'assets.search.noResultsBefore': 'We found no assets for',
  'assets.search.noResultsAfter':
    '. Check the spelling, or try the tag number, the brand or the person name.',
  'assets.detail.loadError': 'Could not load the asset: {message}',
  'assets.detail.plate': 'Tag number',
  'assets.detail.serial': 'Serial number',
  'assets.detail.model': 'Model',
  'assets.detail.location': 'Location',
  'assets.detail.purchaseDate': 'Purchase date',
  'assets.detail.vatPaid': 'VAT paid',
  'assets.detail.noImage': 'No image',
  'assets.detail.byUser': '· by {email}',
  'assets.detail.deleteTitle': 'Delete asset',
  'assets.detail.deleteConfirm': 'Delete the asset with tag #{plate}? This cannot be undone.',
  'assets.field.plate': 'Tag',
  'assets.field.name': 'Name',
  'assets.field.custody': 'Location type',
  'assets.field.paysVat': 'VAT paid',
  'assets.field.photo': 'Photo',
  'assets.action.create': 'Asset created',
  'assets.action.update': 'Asset updated',
  'assets.action.delete': 'Asset deleted',
  'assets.list.empty': 'No assets found with the current filters.',
  // --- Asset Control: form, reports, map and roles -------------------------
  'assets.form.locationOfAsset': 'Asset location',
  'assets.form.selectCategory': 'Choose a category',
  'assets.form.officeLocation': 'Location',
  'assets.form.selectOfficeLocation': 'Choose the location',
  'assets.form.brandRequired': 'Enter a brand',
  'assets.form.searchPerson': 'Search by name or email',
  'assets.form.photo': 'Photo',
  'assets.form.dropImage': 'Drag and drop the image here, or click to upload it',
  'assets.form.takePhoto': 'Take a photo with the camera',
  'assets.form.savedButPhotoFailed':
    'The asset was saved, but the image could not be uploaded.',
  'assets.reports.title': 'Reports',
  'assets.reports.subtitle': 'Asset inventory summary and CSV download.',
  'assets.reports.downloadCsv': 'Download the inventory as CSV',
  'assets.reports.total': 'Total assets',
  'assets.reports.withVat': 'With VAT',
  'assets.reports.withoutVat': 'Without VAT',
  'assets.reports.withPhoto': 'With a photo',
  'assets.reports.byCategory': 'Assets by category',
  'assets.reports.byLocation': 'Assets by location',
  'assets.reports.noData': 'No data.',
  'assets.reports.count': 'Count',
  'assets.map.hint': 'Drag the map to explore it, and tap an area to see its assets.',
  'assets.roles.restricted': 'Access restricted',
  'assets.roles.superAdminOnly': 'Only a SUPER_ADMIN can manage roles.',
  'assets.roles.title': 'Role management',
  'assets.roles.subtitle': 'Grant and revoke administration permissions by email.',
  'assets.roles.assign': 'Assign a role',
  'assets.roles.userEmail': 'User\'s email',
  'assets.roles.filter': 'Filter by email or role',
  'assets.roles.colRole': 'Role',
  'assets.roles.colAssignedOn': 'Assigned on',
  'assets.roles.revokeTitle': 'Revoke role',
  'assets.roles.revokeConfirm': 'Revoke the role',
  // --- Asset Control: photo upload dialog ----------------------------------
  'assets.form.formats': 'Supported formats: PNG, JPG.',
  'assets.form.uploadFailedTitle': 'We could not upload the image',
  'assets.form.uploadFailedBody':
    'The asset was saved, but the image could not be uploaded. Do you want to keep the asset without an image, or retry the upload?',
  // --- Admin: dashboard ----------------------------------------------------
  'admin.occupancy': 'VML occupancy',
  'admin.occupancyMap': 'VML MAP occupancy',
  'admin.enabledStations': 'Enabled workstations',
  'admin.noServiceToday': 'No service today',
  'admin.reusedAvailable': 'Reused: {reused} · Available: {available}',
  'admin.sendLateReportTitle': 'Email the late-cancellation report',
  'admin.sending': 'Sending…',
  'admin.stationBlocking': 'Workstation blocking',
  'admin.stationBlockingDesc':
    'Blocking a workstation is permanent: it does not depend on the selected date and stays until it is enabled again.',
  'admin.doubleClickHint': 'Double-click a workstation to block or enable it',
  'admin.blockStation': 'Block workstation',
  'admin.blockConfirm': 'Block workstation {station}?',
  // --- Admin: dashboard (paired forms) -------------------------------------
  'admin.enableStation': 'Enable workstation',
  'admin.enableConfirm': 'Enable workstation {station}?',
  'admin.sendReport': 'Email the report',
  // --- Admin: settings -----------------------------------------------------
  'admin.settings': 'Settings',
  'admin.occupancyLimit': 'Maximum occupancy limit',
  'admin.searchStation': 'Search for a workstation…',
  'admin.allStations': 'All',
  'admin.enabledOnes': 'Enabled',
  'admin.disabledOnes': 'Disabled',
  // --- Admin: asset categories ---------------------------------------------
  'admin.cat.loadError': 'The categories could not be loaded.',
  'admin.cat.title': 'Asset categories',
  'admin.cat.desc':
    'Manage the asset categories. Deactivating one hides it when creating assets, without touching the assets that already use it.',
  'admin.cat.new': 'New category',
  'admin.cat.englishName': 'English name (optional)',
  'admin.cat.englishShort': 'In English (optional)',
  'admin.cat.add': 'Add category',
  'admin.cat.empty': 'No categories yet.',
  // --- Admin: CSV asset import ---------------------------------------------
  'admin.import.title': 'Import assets (CSV)',
  'admin.import.intro':
    'Upload a CSV to create several assets at once. If any tag number is repeated — inside the file or already in the system — no row is imported.',
  'admin.import.columnsIntro': 'Columns:',
  'admin.import.noneImported': 'No assets were imported. Fix these rows and try again:',
  // --- Admin: CSV import column notes --------------------------------------
  'admin.import.required': 'required',
  'admin.import.requiredUse': 'required; use',
  'admin.import.forHomeAssets': 'for assets kept at the person’s home',
  'admin.import.yesNo': 'Sí/No',
  'admin.import.assignedToNote':
    'email or full name; required when the location is Casa Colaborador',
  'admin.import.mustMatch': 'Categoría and Ubicación must match names that already exist.',
  // --- Admin: day planning, closures, events and lunch days ----------------
  'admin.day.intro':
    'Plan a single day: close the office for holidays or special events, and announce Connects, celebrations or any other notice. Pick a day on the calendar to see and edit its detail.',
  'admin.day.upcomingClosures': 'Upcoming office closures',
  'admin.day.upcomingEvents': 'Upcoming events',
  'admin.day.weekend': 'Weekend — the office is closed by default.',
  'admin.day.officeClosed': 'Office closed on this day',
  'admin.day.reasonLabel': 'Reason:',
  'admin.day.closeOffice': 'Close the office on this day',
  'admin.day.eventsOfDay': 'Events for the day',
  'admin.day.noEvents': 'No events for this day.',
  'admin.day.reasonRequired': 'Reason (required)',
  'admin.day.reasonPlaceholder': 'e.g. Public holiday, closed for maintenance…',
  'admin.day.eventTitle': 'Title',
  'admin.day.eventDescription': 'Description (optional)',
  'admin.day.reasonOptional': 'Reason (optional)',
  'admin.day.selectDayEvents':
    'Pick a day on the calendar to see its events. Double-click to add one.',
  'admin.day.selectDayDetail':
    'Pick a day on the calendar to see its detail. Double-click to edit it.',
  'admin.day.upcomingNoLunch': 'Upcoming days without lunch',
  'admin.day.fridayNoLunch': 'Friday (no lunch by default)',
  'admin.day.noLunchService': 'No lunch service on this day',
  'admin.day.noLunchReservations': 'No lunch reservations for this day',
  // --- Admin: events and closures intros -----------------------------------
  'admin.events.intro':
    'Pick a day on the calendar to see its events. Add a Connect (All Hands), a celebration or any announcement: it is highlighted on the calendar and shown on the map for everybody.',
  'admin.closures.intro':
    'Pick a day on the calendar to see its detail. Double-click to mark or unmark an office closure (holiday, special event, and so on).',
  // --- Admin: lunch-disabled days ------------------------------------------
  'admin.lunch.intro':
    'Pick a day on the calendar to see its detail. Double-click to turn the lunch service on or off.',
  'admin.lunch.timeOfDay': ' · {time} {when}',
  'admin.lunch.today': 'today',
  'admin.lunch.yesterday': 'yesterday',
  'admin.lunch.byPerson': 'by {name}',
  'admin.lunch.reasonPlaceholder': 'e.g. Public holiday, special event…',
  // --- Admin: reservation history ------------------------------------------
  'admin.hist.user': 'User',
  'admin.hist.editSeat': 'Edit seat',
  'admin.hist.editLunch': 'Edit lunch',
  // --- Admin: create a reservation for somebody else -----------------------
  'admin.create.title': 'Book for somebody else',
  'admin.create.subtitle': 'Assign a workstation (and lunch) to a colleague or a visitor.',
  'admin.create.cta': 'Create reservation',
  'admin.create.forWhom': 'Who is it for?',
  'admin.create.newVmlHint':
    'If the VML address does not exist yet, the account is created automatically (their next Okta sign-in will work).',
  'admin.create.visitorName': 'Visitor\'s name',
  'admin.create.visitorNoteBefore':
    'They are booked as external. No account is created, and the map will show “Taken by:',
  'admin.create.visitorNoteAfter': '(External)”.',
  'admin.create.namePlaceholder': 'Name',
  'admin.create.availableStation': 'Available workstation',
  'admin.create.stationCount.one': '{count} workstation available on {date}.',
  'admin.create.stationCount.other': '{count} workstations available on {date}.',
  // --- Admin: change station dialog ----------------------------------------
  'admin.change.currentStation': 'Current workstation:',
  'admin.change.noneAvailable': 'No workstations available that day',
  'admin.change.newStation': 'New workstation',
  'admin.change.count.one':
    '{count} workstation available. The person is emailed their new workstation.',
  'admin.change.count.other':
    '{count} workstations available. The person is emailed their new workstation.',
  // --- Admin: extra lunches ------------------------------------------------
  'admin.extra.empty':
    'No extra lunches assigned. Use "Assign" when a lunch is left over from a late cancellation, or to add one on a full day (before 2:30 PM the previous day).',
  'admin.extra.resendQr': 'Resend the QR by email',
  'admin.extra.removeAssignment': 'Remove assignment',
  'admin.extra.dialogTitle': 'Assign an extra lunch',
  'admin.extra.dialogHint':
    'Assign a leftover lunch (from a late cancellation, for example) to somebody else. If the day is full and there are no leftovers, you can still add one while there is enough notice (before 2:30 PM the previous day). The recipient is emailed their QR code if you pick a user with an address.',
  'admin.extra.notInList': 'Not on the list? Enter the name by hand:',
  'admin.extra.note': 'Note (optional)',
  'admin.extra.notePlaceholder': 'e.g. Left over from a late cancellation by Juan Pérez',
  'admin.extra.sendQr': 'Email the QR code to the recipient',
  // --- Admin: lunch reassignment and user management -----------------------
  'admin.reassign.hint':
    'The lunch has already been ordered. Reassigning it invalidates its QR code, and the food goes to the person you pick.',
  'admin.reassign.notInList': 'Not on the list? Enter the details by hand.',
  'admin.reassign.sendQr': 'Email the QR code to the new person',
  'admin.users.search': 'Search by name or email…',
  'admin.users.allRoles': 'All',
  'admin.users.admins': 'Admins',
  'admin.users.users': 'Users',
  'admin.users.noRole': 'No role assigned',
  'admin.users.loadError': 'Could not load the users. Please try again later.',
  'admin.users.none': 'No users found',
  'admin.users.lastSeenToday': 'Last access: today at {time}',
  'admin.users.lastSeenOn': 'Last access: {date}, {time}',
  'admin.users.lastSeenNever': 'No recorded access',
  'admin.users.allPermissions': 'every permission',
  'admin.users.allPermissionsTitle':
    'SUPER_ADMIN includes every permission, including any added later',
  'admin.users.makeAdmin': 'Make Admin',
  'admin.users.confirmTitle': 'Confirm',
  'admin.users.confirmBody': 'Are you sure you want to {action} {name}?',
  'admin.users.disableWarning':
    'All their future reservations (workstation and lunch) will be cancelled and they will lose access to the system.',
  'admin.users.revokeTitle': 'Revoke role',
  'admin.users.revokeBody': 'Revoke the {role} role from {email}?',
  'admin.users.action.makeAdmin': 'promote to Admin',
  'admin.users.action.removeAdmin': 'remove the Admin role from',
  'admin.users.action.disable': 'disable',
  'admin.users.action.enable': 'enable',
  'admin.users.action.editName': 'edit the name of',
  // --- Admin, assets and shared UI — bulk pass -----------------------------
  'common.delete': 'Delete',
  'common.next': 'Next',
  'g.coverage': 'Coverage',
  'g.certifications': 'Certifications',
  'g.downloadCsv': 'Download CSV',
  'g.activeOnly': 'Active only',
  'g.today': 'Today',
  'g.loading': 'Loading…',
  'g.loadingEllipsis': 'Loading…',
  'g.savingEllipsis': 'Saving…',
  'g.deleting': 'Deleting…',
  'g.importing': 'Importing…',
  'g.revoking': 'Revoking…',
  'g.uploadingImage': 'Uploading image…',
  'g.required': 'Required',
  'g.requiredField': '*Required field',
  'g.max50': 'Maximum 50 characters',
  'g.max100': 'Maximum 100 characters',
  'g.unexpectedError': 'Unexpected error',
  'g.unexpectedErrorLong': 'An unexpected error occurred.',
  'g.noServerResponse': 'No response from the server.',
  'g.accessDenied': 'Access denied',
  'g.of': 'of',
  'g.nextArrow': 'Next →',
  'g.wed': 'Wed',
  'g.sat': 'Sat',
  'g.inactives': 'Inactive',
  'g.inactiveParen': '(inactive)',
  'g.pending': 'Pending',
  'g.notifications': 'Notifications',
  'g.nameOptional': 'Name (optional)',
  'g.nameOrEmail': 'Name or email',
  'g.searchUser': 'Search for a user',
  'g.emailExample': 'user@vml.com',
  'g.emailPattern': 'first.last@vml.com',
  'g.assignedEmail': 'Assigned email',
  'g.noFilterMatch': 'Nothing matches that filter.',
  'g.reservations': 'Reservations',
  'g.lunches': 'Lunches',
  'g.planning': 'Planning',
  'g.users': 'Users',
  'g.assets': 'Assets',
  'g.workstations': 'Workstations',
  'g.reservation': 'Reservation',
  'g.booked': 'Booked',
  'g.parking': 'Parking',
  'g.steady': 'Steady',
  'g.moderate': 'Moderate',
  'g.maximum': 'Maximum',
  'g.generateReport': 'Generate report',
  'g.adminStaff': 'Administrative staff',
  'g.visitor': 'Visitor',
  'g.existingUser': 'Existing user',
  'g.otherVmlEmail': 'Someone else (VML address)',
  'g.includeLunch': 'Include lunch',
  'g.reassignLunch': 'Reassign lunch',
  'g.reassigned': 'Reassigned',
  'g.markDelivered': 'Mark as picked up',
  'g.markNotDelivered': 'Mark as not picked up',
  'g.markVegetarian': 'Mark as vegetarian',
  'g.vegetarianClickRemove': 'Vegetarian (click to remove)',
  'g.noLunchService': 'No lunch service',
  'g.fridayNoLunch': 'Friday — no lunch',
  'g.lunchLower': 'lunch',
  'g.lunchesTotal': 'lunches in total',
  'g.assignedToLower': 'assigned to',
  'g.cancelledLower': 'cancelled',
  'g.userLower': 'user',
  'g.takenLower': 'taken',
  'g.officeClosed': 'Office closed',
  'g.officeOpen': 'Office open — reservations allowed',
  'g.closeOffice': 'Close the office',
  'g.reopenOffice': 'Reopen the office',
  'g.pastDate': 'Past date',
  'g.addEvent': '+ Add event',
  'g.add': '+ Add',
  'g.deleteEvent': 'Delete event',
  'g.eventTitleRequired': 'The event title is required.',
  'g.officeMap': 'Office map',
  'g.baseMonth': 'Base month',
  'g.comparedMonth': 'Compared month',
  'g.comment': 'Comment',
  'g.editName': 'Edit name',
  'g.nameUpdated': 'Name updated.',
  'g.assignedRoles': 'Assigned roles',
  'g.noRoles': 'No roles assigned.',
  'g.adminRoleRemoved': 'Admin role removed.',
  'g.userPromoted': 'User promoted to Admin.',
  'g.userEnabled': 'User enabled.',
  'g.userDisabled': 'User disabled. Their future reservations were cancelled.',
  'g.roleAssignError': 'The role could not be assigned.',
  'g.qrResent': 'QR code resent.',
  'g.lunchReportSent': 'Lunch report sent.',
  'g.reportSendFailed': 'The report could not be sent.',
  'g.closureSaved': 'Office closure saved.',
  'g.closureSaveFailed': 'The closure could not be saved.',
  'g.closureDeleted': 'Office closure removed.',
  'g.closureDeleteFailed': 'The closure could not be removed.',
  'g.eventCreated': 'Event created.',
  'g.eventCreateFailed': 'The event could not be created.',
  'g.eventDeleted': 'Event removed.',
  'g.eventDeleteFailed': 'The event could not be removed.',
  'g.categoryCreateFailed': 'The category could not be created.',
  'g.categoryUpdateFailed': 'The category could not be updated.',
  'g.saveFailed': 'The change could not be saved.',
  'g.stationChangeFailed': 'The workstation could not be changed.',
  'g.stationChanged': 'The workstation was changed.',
  'g.reservationCreated': 'Reservation created.',
  'g.reservationCreateFailed':
    'The reservation could not be created: the workstation is already taken, or the person already has one that day.',
  'g.pickUser': 'Pick a user.',
  'g.pickUserOrName': 'Pick a user, or type a name.',
  'g.pickPersonOrName': 'Pick a person, or type a name.',
  'g.pickStation': 'Pick an available workstation.',
  'g.enterVisitorName': 'Enter the visitor\'s name.',
  'g.enterVmlEmail': 'Enter a valid VML address.',
  'g.addAsset': 'Add asset',
  'g.editAsset': 'Edit asset',
  'g.saveAsset': 'Save asset',
  'g.saveChanges': 'Save changes',
  'g.saveWithoutImage': 'Save without the image',
  'g.assetNotFound': 'Asset not found.',
  'g.assetCreated': 'Asset created.',
  'g.assetUpdated': 'Asset updated.',
  'g.assetCreateFailed': 'The asset could not be created.',
  'g.assetUpdateFailed': 'The asset could not be updated.',
  'g.imageUploaded': 'Image uploaded',
  'g.cameraDenied': 'Camera permission denied.',
  'g.cameraFailed': 'The camera could not be opened.',
  'g.noChangesRecorded': 'No changes recorded.',
  'g.noLocation': 'No location',
  'g.requiredForHomeAssets': 'Required for assets kept at home',
  'g.assetAtHomeNote':
    'The asset is at the assigned person’s home, so it has no office location.',
  'g.assetInOfficeNote': 'The asset is in the office — say where.',
  'g.searchingAssets': 'Searching assets…',
  'g.searchingAssetsDetail': 'Checking tag numbers, brands, serials and assigned people.',
  'g.importFailed': 'The file could not be imported.',
  'g.fileReadFailed': 'The file could not be read.',
  'g.templateFileName': 'asset-template.xlsx',
  'g.purchaseDateLower': 'purchase date',
  'g.people': 'People',
  'g.myTeam': 'My team',
  'g.currentProject': 'Current project',
  'g.noProject': 'No project',
  'g.noProjectsRecorded': 'No projects recorded.',
  'g.noCertsRecorded': 'No certifications recorded.',
  'g.workingOn': 'Working on',
  'g.updated': 'Updated',
  'g.added': 'Added',
  'g.backToPeople': '← Back to people',
  'g.endBeforeStart': 'The end date cannot be earlier than the start date.',
  'g.surveysCompleted': 'lunch surveys completed.',
  'loc.reception': 'Reception',
  'loc.receptionEntrance': 'Reception Entrance',
  'loc.receptionExit': 'Reception Exit',
  'loc.entrance': 'Entrance',
  'loc.exit': 'Exit',
  'loc.printing': 'Printing',
  'loc.printingArea': 'Printing Area',
  'loc.totoArea': 'ToTo Area',
  'loc.relaxArea': 'Relax Area',
  'loc.techManagement': 'Technology Management',
  'loc.roomShower': 'Meeting Room The Shower',
  'loc.room506': 'Meeting Room 506',
  'loc.roomChurchill': 'Meeting Room Churchill',
  'loc.roomLaPulpe': 'Meeting Room La Pulpe',
  'loc.roomTuanis': 'Meeting Room Tuanis',
  'g.updatedOn': 'Updated on',
  'g.createdOn': 'Created on',
  'g.administrative': 'Administrative',
  'g.extraLunches': 'Extra lunches',
  'g.assigned': 'Assigned',
  'g.assigning': 'Assigning…',
  'g.assignmentRemoved': 'Assignment removed.',
  'g.extraLunchAssigned': 'Extra lunch assigned.',
  'g.lunchReassigned': 'Lunch reassigned.',
  'g.lunchDisabled': 'Lunch service turned off.',
  'g.lunchEnabled': 'Lunch service turned back on.',
  'g.disableLunch': 'Turn lunch off',
  'g.searchingAssetsShort': 'Searching assets',
  'g.calculatingLunches': 'Working out available lunches',
  'g.uploadingImageShort': 'Uploading image',
  'g.cancelledFem': 'Cancelled',
  'g.employeeHome': 'Employee home',
  'g.categoryCreated': 'Category created.',
  'g.categoryUpdated': 'Category updated.',
  'g.categoryEnabled': 'Category enabled.',
  'g.categoryDisabled': 'Category disabled.',
  'g.officeClosures': 'Office closures',
  'g.peopleColon': 'People:',
  'g.emailOptional': 'Email (optional)',
  'g.vmlEmail': 'VML email',
  'g.anyStatus': 'Any status',
  'g.downloadTemplate': 'Download the template',
  'g.disabled': 'Disabled',
  'g.disabledPlural': 'Disabled',
  'g.enabledFem': 'Enabled',
  'g.fileNoRows': 'The file has no data rows.',
  'g.lunchSlotUnavailable': 'The selected lunch time is not available.',
  'g.invalidEmail': 'Invalid email address.',
  'g.workstationsTitle': 'Workstations',
  'g.station1': 'Workstation 1',
  'g.officeStatus': 'Office status',
  'loc.opsManagement': 'Operations Management',
  'g.reservationHistory': 'Reservation history',
  'g.adminLunchUpdateFailed': 'The administrative lunch could not be updated.',
  'g.stationUpdateFailed': 'The workstation could not be updated.',
  'g.userUpdateFailed': 'The user could not be updated.',
  'g.extraLunchFailed': 'The extra lunch could not be assigned.',
  'g.lunchLoadFailed': 'The lunch data could not be loaded.',
  'g.reservationFailed': 'The reservation could not be created.',
  'g.lunchDisableFailed': 'Lunch service could not be turned off.',
  'g.lunchEnableFailed': 'Lunch service could not be turned back on.',
  'g.userDisableFailed': 'The user could not be disabled.',
  'g.userEnableFailed': 'The user could not be enabled.',
  'g.deleteFailed': 'It could not be deleted.',
  'g.reportFailed': 'The report could not be sent.',
  'g.qrSendFailed': 'The QR codes could not be sent.',
  'g.reassignFailed': 'The lunch could not be reassigned.',
  'g.useVmlOption':
    'That is a VML address. Use the “Someone else (VML address)” option to book on a colleague’s behalf.',
  'g.reservationConflict':
    'The reservation could not be created: the workstation is already taken, or the person already has one that day.',
  // --- Asset map tooltip count ---------------------------------------------
  'map.assetCount.one': '· {count} asset',
  'map.assetCount.other': '· {count} assets',
  // --- Leftovers found by the widened guard --------------------------------
  'map.availableOf': 'of {total} workstations available',
  'skills.staleNote': 'Updated {since}',
  'skills.staleWorth': ' — worth a look',
  'reports.tab.lunch': 'Lunches',
  'reports.tab.deskbooking': 'Deskbooking',
  'reports.tab.assets': 'Assets',
  'reports.survey.completed': 'lunch surveys completed {period}.',
  // --- Location groups and the last stragglers -----------------------------
  'locgroup.estacion': 'Workstations',
  'locgroup.sala': 'Meeting rooms',
  'locgroup.gerencia': 'Management offices',
  'locgroup.oficina': 'Offices',
  'locgroup.zonaComun': 'Common areas',
  'locgroup.comedor': 'Dining room',
  'locgroup.departamento': 'Departments',
  'locgroup.bodega': 'Storage rooms',
  'locgroup.mantenimiento': 'Maintenance',
  // --- Assets: disposal ----------------------------------------------------
  'assets.side.active': 'In use',
  'assets.side.disposed': 'Disposed',
  'assets.disposed.title': 'Disposed assets',
  'assets.disposed.empty': 'No disposed assets match the current filters.',
  'assets.col.disposedAt': 'Disposed on',
  'assets.col.disposalReason': 'Reason',
  'assets.action.dispose': 'Disposed',
  'assets.action.restore': 'Restored',
  'assets.disposal.badge': 'Disposed',
  'assets.disposal.action': 'Dispose',
  'assets.disposal.restore': 'Restore',
  'assets.disposal.bannerLine': 'Disposed on {date} · {reason}',
  'assets.disposal.dialogTitle': 'Dispose of this asset',
  'assets.disposal.dialogBody':
    'Tag {plate} will stop showing in {location} and in the map counts. The record is kept and you can restore it later.',
  'assets.disposal.reasonLabel': 'Reason',
  'assets.disposal.noteLabel': 'Note (optional)',
  'assets.disposal.done': 'Asset disposed.',
  'assets.disposal.restored': 'Asset restored.',
  'assets.disposal.restoreTitle': 'Restore this asset',
  'assets.disposal.restoreBody':
    'Tag {plate} goes back into the inventory and will show in {location} again.',
  'assets.disposal.reason.nationalised': 'Nationalised',
  'assets.disposal.reason.damaged': 'Damaged',
  'assets.disposal.reason.obsolete': 'Obsolete',
  'assets.disposal.reason.sold': 'Sold',
  'assets.disposal.reason.lost': 'Lost',
  'assets.disposal.reason.other': 'Other',
  'assets.reports.disposed': 'Disposed',
  'assets.reports.downloadDisposedCsv': 'Download CSV',
  'booking.lunchCheckFailed':
    'We could not check lunch availability. Please try again.',
  'admin.stationBlocked': 'Workstation {station} blocked.',
  'admin.stationEnabled': 'Workstation {station} enabled.',
  // --- Office map cell labels ----------------------------------------------
  'loc.creativeManagement': 'Creative Management',
  'loc.financeManagement': 'Finance Management',
  'loc.generalManagement': 'General Management',
  'loc.pmManagement': 'PM Management',
  'loc.hrManagement': 'HR Management',
  'loc.accountsManagement': 'Accounts Management',
  'loc.hrOffices': 'HR Offices',
  'loc.itDepartment': 'IT Department',
  'loc.kitchenDining': 'Kitchen & Dining',
  'loc.tuanisRoom': 'Tuanis Room',
  'loc.churchillRoom': 'Churchill Room',
  'loc.laPulpeRoom': 'La Pulpe Room',
  // Short form; 'loc.room506' already holds the full "Meeting Room 506".
  'loc.room506Short': 'Room 506',
  'loc.nursingRoom': 'Nursing Room',
  'loc.lockersHallway': 'Lockers Hallway',
  'loc.managementHallway': 'Management Hallway',
  'admin.lunchSplit': 'People: {people} · Admin: {admin}',
  'g.dateLabel': 'Date:',
  // --- Plural counts in the lunch and user panels --------------------------
  'admin.lunchCount.one': '{count} lunch',
  'admin.lunchCount.other': '{count} lunches',
  'admin.reassignedTo': 'Reassigned to {name}',
  // --- Actions menu on an administrative lunch -----------------------------
  'adminLunch.actions': 'Lunch actions for {name}',
  'adminLunch.reassign': 'Reassign',
  'adminLunch.cancel': 'Cancel lunch',
  'adminLunch.restore': 'Restore lunch',
  'adminLunch.cancelledBadge': 'Cancelled{time}',
  'adminLunch.cancelTitle': 'Cancel lunch',
  'adminLunch.cancelQuestion': 'Cancel {name}\'s lunch? It was already ordered from the supplier, so it counts as a late cancellation and becomes available as an extra lunch.',
  'adminLunch.cancelled': '{name}\'s lunch was cancelled.',
  'adminLunch.restored': '{name}\'s lunch was restored.',
  'admin.availableLunches.one': '{count} lunch available',
  'admin.availableLunches.other': '{count} lunches available',
  'admin.lateCancelledCount.one': '{count} cancelled late',
  'admin.lateCancelledCount.other': '{count} cancelled late',
  'admin.alreadyAssigned.one': '{count} already assigned',
  'admin.alreadyAssigned.other': '{count} already assigned',
  'admin.userCount.one': '{count} user',
  'admin.userCount.other': '{count} users',
  'admin.calculatingLunches': 'Working out the available lunches…',
  // --- Skill Matrix: closed enums shown to people --------------------------
  'skl.level0': 'No knowledge',
  'skl.level0Criteria':
    'I have not used the technology and my grasp of its core concepts is limited.',
  'skl.level1': 'Basic',
  'skl.level1Criteria':
    'I understand the core concepts and can carry out simple tasks by following documentation, with support.',
  'skl.level2': 'Intermediate',
  'skl.level2Criteria':
    'I work independently on day-to-day tasks. I solve routine problems and know where to look when something new comes up.',
  'skl.level3': 'Advanced',
  'skl.level3Criteria':
    'I design solutions, make technical decisions and support the team through complex situations. I can share knowledge and coach others.',
  'skl.disc.development': 'Development',
  'skl.disc.content': 'Content',
  'skl.disc.analysis': 'Analysis',
  'skl.disc.systems': 'Systems',
  'skl.disc.architecture': 'Architecture',
  'skl.lang.english': 'English',
  'skl.lang.portuguese': 'Portuguese',
  'skl.lang.french': 'French',
  'skl.lang.spanish': 'Spanish',
  'skl.lang.german': 'German',
  'skl.lang.italian': 'Italian',
  'skl.cefr.a1': 'A1 · Basic',
  'skl.cefr.a2': 'A2 · Elementary',
  'skl.cefr.b1': 'B1 · Intermediate',
  'skl.cefr.b2': 'B2 · Upper intermediate',
  'skl.cefr.c1': 'C1 · Advanced',
  'skl.cefr.c2': 'C2 · Native or equivalent',
  // --- Projects panel and the last-updated indicator -----------------------
  'skills.projects': 'Projects',
  'skills.current': 'Current',
  'skills.past': 'Past',
  'upd.seconds': 'Updated a few seconds ago',
  'upd.secondsN': 'Updated {count}s ago',
  'upd.minute': 'Updated 1 minute ago',
  'upd.minutes': 'Updated {count} minutes ago',
  'upd.hour': 'Updated 1 hour ago',
  'upd.hours': 'Updated {count} hours ago',
  // --- Role and custody labels ---------------------------------------------
  'role.superAdmin': 'Super Admin',
  'role.deskbookingAdmin': 'Deskbooking',
  'role.lunchAdmin': 'Lunch',
  'role.assetAdmin': 'Assets Admin',
  'role.assetViewer': 'Assets Viewer',
  'role.reportsViewer': 'Reports',
  'role.skillsAdmin': 'Skills Admin',
  'role.skillsViewer': 'Skills Tech',
  'custody.office': 'Office',
  'custody.employeeHome': 'Employee\'s home',
  // --- Role assignment messages --------------------------------------------
  'admin.roleRemoved': 'Role {role} removed from {email}.',
  'admin.roleAssigned': 'Role {role} assigned to {email}.',
  'admin.revokeAria': 'Revoke {role}',
  // --- The last of the Spanish, found by scanning all of src ---------------
  'x.floorOccupancy': 'Floor occupancy',
  'x.loading': 'Loading',
  'x.reservationOf': 'Reservation for',
  'x.workstations': 'Workstations',
  'x.meetingRooms': 'Meeting rooms',
  'x.wentWrong': 'Something went wrong',
  'x.unexpectedReload': 'An unexpected error occurred. Please reload the page.',
  'x.reloadPage': 'Reload the page',
  'x.loadFailed': 'We could not load the data. Check your connection and try again.',
  'x.officeFull': 'Office full',
  'x.noStationsFor': 'No workstations available for',
  'x.willEmailYou': 'We will email you when a spot opens up.',
  'x.registering': 'Signing you up…',
  'x.cancelling': 'Cancelling…',
  'x.occupancyHelp': 'Set the percentage of the daily capacity of people allowed on site.',
  'x.occupancyPercent': 'Allowed capacity percentage',
  'x.bookWorkstation': 'Book a workstation',
  'x.techFeatures': 'Technical features',
  'x.reservationDate': 'Reservation date',
  'x.reservationTime': 'Reservation time',
  'x.chooseStation': 'Choose a workstation',
  'x.bookStation': 'Book the workstation',
  'x.confirmReservation': 'Confirm the reservation',
  'x.redirectingOkta': 'Redirecting you to the Okta sign-in page',
  'x.maintenance': 'We are making improvements to the system.',
  'x.workingOnIt': 'Working on it',
  'x.blockedUser': 'Your account is temporarily blocked',
  'x.contactAdmin': 'Please get in touch with an administrator. Thank you!',
  'x.photoFormat': 'Unsupported format. PNG or JPG only.',
  'x.photoSize': 'The file is over the 5 MB limit.',
  'vf.verifyData': 'Please check that the pre-filled details are correct',
  'vf.fullName': 'Full name',
  'vf.phone': 'Phone number',
  'vf.travelled': 'Have you travelled outside the country in the last 5 days?',
  'vf.orangeAlert':
    'Have you travelled in the last 5 days to any canton under an orange alert?',
  'vf.cantonVisited': 'Which canton did you visit?',
  'vf.returnDate': 'What date did you return?',
  'vf.closeContact':
    'Have you been in close contact with a suspected or confirmed COVID-19 case?',
  'vf.symptoms': 'Are you currently experiencing any of the following COVID-19-like symptoms?',
  'vf.vaccinated': 'Have you completed your vaccination schedule?',
  'roled.superAdmin':
    'Full access to the whole platform. Includes every other role, even ones added in the future.',
  'roled.deskbookingAdmin':
    'Manages reservations and workstations. Also has access to Asset Control and Reports.',
  'roled.lunchAdmin': 'Manages the lunch module: orders, validation and lunch reports.',
  'roled.assetAdmin':
    'Creates, edits and deletes assets in Asset Control. Includes its reports.',
  'roled.assetViewer': 'Views Asset Control and its reports. Cannot create or change assets.',
  'roled.reportsViewer': 'Views the Reports section. Administers nothing.',
  'roled.skillsAdmin':
    'Manages the Skill Matrix: views and edits every profile, the coverage report, and adds skills to the catalogue.',
  'roled.skillsViewer':
    'Marks a person as technical staff: they can view and edit their own skills profile. They do not see anybody else’s.',
  // --- Card and department line --------------------------------------------
  'x.reservationFor': 'Reservation for {name}',
  'x.department': 'Department: {name}',
  // --- Full-capacity modal and occupancy slider ----------------------------
  'x.noStationsOn': 'No workstations available on',
  'x.dateLongFormat': 'dddd, MMMM DD',
  'x.occupancyHelpNoDot': 'Set the percentage of the daily capacity of people allowed on site',
  'x.station2G05': 'Workstation 2G05',
  'x.from': 'From:',
  // --- Visit form and standalone pages -------------------------------------
  'vf.orangeAlertLong':
    'Have you travelled in the last 5 days to a canton under an orange alert because of a COVID-19 outbreak?',
  'vf.orangeAlertLink': 'Check the cantons under orange alert',
  'vf.closeContactLong':
    'Have you been in close contact with a suspected or confirmed COVID-19 case in the last 14 days?',
  'vf.symptomsLong':
    'Are you currently experiencing any of these flu-like symptoms? (fever, cough, shortness of breath, loss of taste, loss of smell.)',
  'vf.declaration':
    'I acknowledge that all the information I have provided in this form is correct, truthful and complete.',
  'x.backSoon': 'We will be back soon.',

  // --- Skill Matrix: managers, freelancers, preview, countries (2026-10) ---
  'country.CR': 'Costa Rica',
  'country.GT': 'Guatemala',
  'g.prevArrow': '← Previous',
  'skills.anyCountry': 'Any country',
  'skills.back': '← Back',
  'skills.retry': 'Try again',
  'skills.loadFailed': 'We could not load the Skill Matrix. Check your connection and try again.',
  'skills.pageRange': '{from}–{to} of {total}',
  'skills.pastProjects': 'Previous experience',
  'skills.confirmDelete': 'Sure? Delete',
  'skills.stillCurrent': 'Still accurate',
  'skills.newInGridDone': 'Done, I reviewed them',
  'skills.field.manager': 'Manager',
  'skills.tab.directory': 'Directory',
  'skills.tab.myTeam': 'My team',
  'skills.tab.teams': 'Teams',
  'skills.project.end': 'End',
  'skills.add.createFailed': 'The skill could not be created.',
  'skills.chart.peopleSeries': 'People',
  'skills.chart.persons.one': '{count} person',
  'skills.chart.persons.other': '{count} people',
  'skills.profile.country': 'Country',
  'skills.profile.manager': 'Manager (email)',
  'skills.profile.managerPlaceholder': 'first.last@vml.com',
  'skills.profile.freelanceEmailHint':
    'With their VML email they go straight to their skills form, and see nothing else in the app.',
  'skills.create.title': 'Add person',
  'skills.create.save': 'Add',
  'skills.create.email': 'VML email',
  'skills.create.emailHint': 'This is how the person reaches their profile. You can leave it empty and add it later.',
  'skills.create.freelanceNeedsEmail': 'A freelancer needs their VML email to be able to fill in the form.',
  'skills.create.rolesHint': 'If you leave this empty, they pick their role when they start the form.',
  'skills.manager.title': 'Assign manager',
  'skills.manager.assign': 'Assign',
  'skills.manager.clear': 'Remove the manager from these people',
  'skills.manager.emailRequired': 'Type the manager’s email.',
  'skills.manager.forPeople.one': 'Applies to {count} selected person.',
  'skills.manager.forPeople.other': 'Applies to the {count} selected people.',
  'skills.dir.addPerson': '+ Add person',
  'skills.dir.export': 'Export CSV',
  'skills.dir.anyManager': 'Any manager',
  'skills.dir.showInactive': 'Include inactive',
  'skills.dir.selectAll': 'Select everyone shown',
  'skills.dir.select': 'Select {name}',
  'skills.dir.selection': 'Selected people',
  'skills.dir.selectedCount.one': '{count} selected',
  'skills.dir.selectedCount.other': '{count} selected',
  'skills.dir.assignManager': 'Assign manager',
  'skills.dir.clearSelection': 'Clear selection',
  'skills.dir.noEmail': 'No email',
  'skills.dir.noEmailTitle':
    'Without a linked VML email they cannot sign in to fill in their form. Add it from Edit.',
  'skills.ob.updateTitle': 'Update your skills',
  'skills.ob.updateIntro':
    'Your previous answers are already selected: change only what is different and carry on.',
  'skills.ob.savedAsYouGo': 'Saved as you move on.',
  'skills.fo.greeting': 'Skill Matrix · VML',
  'skills.fo.doneTitle': 'All set! Your skills are saved',
  'skills.fo.doneTitleName': 'All set, {name}! Your skills are saved',
  'skills.fo.doneText':
    'Thank you. If something changes — you learnt something new or levelled up — come back any time and update it.',
  'skills.fo.savedNow': 'Your answers are saved. Thanks for keeping them current!',
  'skills.fo.update': 'Update my answers',
  'skills.fo.newSkills': 'There are {count} new skills for your role you have not answered yet.',
  'skills.fo.answerNew': 'Answer them',
  'skills.fo.yourAnswers': 'What you answered',
  'skills.fo.lastUpdated': 'Last updated: {date}',
  'skills.preview.enter': 'View as colleague',
  'skills.preview.enterTitle': 'See the Skill Matrix exactly as a team member does',
  'skills.preview.badge': 'Colleague view',
  'skills.preview.note': 'This is the Skill Matrix as a team member sees it. Admin options are hidden.',
  'skills.preview.exit': 'Back to admin view',
  'skills.preview.formTab': 'Form',
  'skills.preview.formBadge': 'Preview',
  'skills.preview.formNote': 'Nothing you pick here is saved.',
  'skills.preview.changeRoles': 'Change roles',
  'skills.preview.pickRoles': 'Which role do you want to preview?',
  'skills.preview.pickRolesHelp':
    'Pick one or more roles to see exactly the questions that person gets.',
  'skills.preview.endTitle': 'This is where the form ends',
  'skills.preview.endText':
    'When they finish, the person gets into the Skill Matrix and their answers show on their profile.',
  'skills.preview.again': 'Try another role',
  'skills.preview.noOwnProfile':
    'Your account has no skills profile, so there is no own profile to show. Try the Directory or Form tabs.',
  'skills.review.title': 'Manager review',
  'skills.review.lastOn': 'Reviewed on {date}',
  'skills.review.notDoneYet': 'They have not finished the form yet; you can review it once they do.',
  'skills.review.markReviewed': 'Mark as reviewed',
  'skills.review.addNote': 'Add note',
  'skills.review.editNote': 'Edit note',
  'skills.review.save': 'Save and mark reviewed',
  'skills.review.placeholder':
    'Optional: anything to talk about? For example, a skill you think is higher or lower.',
  'skills.review.fromManager': 'Note from your manager',
  'skills.team.people.one': '{count} person',
  'skills.team.people.other': '{count} people',
  'skills.team.pending': 'Not finished',
  'skills.team.toReview': 'To review',
  'skills.team.reviewed': 'Reviewed',
  'skills.team.progress': 'Progress',
  'skills.team.review': 'Review',
  'skills.team.actions': 'Actions',
  'skills.team.doneOn': 'Done · {date}',
  'skills.team.reviewedOn': 'Reviewed · {date}',
  'skills.team.notReviewed': 'Not reviewed',
  'skills.team.changed': 'Changed since your review',
  'skills.team.remind': 'Remind',
  'skills.team.reminded': 'We sent {name} a reminder.',
  'skills.team.reAsk': 'Ask to update',
  'skills.team.reAskConfirm':
    'Ask {name} to update their skills? They will see the form again with their current answers already selected.',
  'skills.team.reAsked': 'Done: {name} will see the form next time they sign in.',
  'skills.team.open': 'View answers',
  'skills.team.openReview': 'Review',
  'skills.team.empty': 'Nobody has this manager yet.',
  'skills.team.noneInFilter': 'Nobody in this group. All caught up!',
  'skills.team.noManagers':
    'No managers assigned yet. Assign them from People: select people and use "Assign manager".',
  'skills.team.pickManager': 'Pick a manager',
  'skills.team.adminHint': 'You see exactly what that manager sees.',
  'skills.team.teamsTitle': 'Message {name} on Teams',
  'skills.team.githubLabel': '{name} on GitHub',

  // --- Skill Matrix: personal form link (no sign-in) -----------------------
  'skills.link.title': 'Form link (no sign-in)',
  'skills.link.help':
    'For people who cannot sign in to Deskbooking, such as freelancers: it opens a page where they answer only their skills. Personal, and it expires after 90 days.',
  'skills.link.none': 'No link',
  'skills.link.activeUntil': 'Active until {date}',
  'skills.link.send': 'Send by email',
  'skills.link.sendTitle': 'Creates a new link and sends it to {email}',
  'skills.link.sent': 'Done: we emailed the link to {email}.',
  'skills.link.create': 'Create link',
  'skills.link.newLink': 'Create a new link',
  'skills.link.copy': 'Copy',
  'skills.link.copied': 'Copied!',
  'skills.link.onceHint':
    'Copy it now: for security it cannot be shown again. If you lose it, create a new one.',
  'skills.link.replaceHint': 'Creating a new one disables the previous link.',
  'skills.link.revoke': 'Disable',
  'skills.link.revoked': 'The link no longer works.',

  // --- Skill Matrix: dashboard ---------------------------------------------
  'skills.tab.dashboard': 'Overview',
  'skills.dash.showTable': 'Show table',
  'skills.dash.showChart': 'Show chart',
  'skills.dash.everyone': 'All managers',
  'skills.dash.scopeAll': 'The whole office',
  'skills.dash.scopeManager': 'One manager’s team',
  'skills.dash.scopeTeam': 'Your team',
  'skills.dash.completion': 'Form completed',
  'skills.dash.doneOf': '{done} of {total} people',
  'skills.dash.done': 'Done',
  'skills.dash.inProgress': 'In progress',
  'skills.dash.notStarted': 'Pending',
  'skills.dash.reviewedTile': 'Reviewed by their manager',
  'skills.dash.ofDone': 'of {count} done',
  'skills.dash.ofPeople': 'of {count} people',
  'skills.dash.avgRated': 'Skills per person, on average',
  'skills.dash.stale': 'Answers over a year old',
  'skills.dash.withManager': 'With a manager assigned',
  'skills.dash.mismatch': 'Say no role fits them',
  'skills.dash.byRole': 'Progress by role',
  'skills.dash.byRoleSub': 'Someone with two roles counts in both.',
  'skills.dash.byCountry': 'Progress by country',
  'skills.dash.noRole': 'No role yet',
  'skills.dash.total': 'Total',
  'skills.dash.topSkills': 'What the team knows best',
  'skills.dash.topSkillsSub': 'The skills with the most people, by level.',
  'skills.dash.gaps': 'Where coverage is thinnest',
  'skills.dash.gapsSub':
    'Of the people who finished a form that asks the skill, the share at Intermediate or above.',
  'skills.dash.gapValue': '{strong} of {asked} at Intermediate or above',
  'skills.dash.asked': 'Asked of',
  'skills.dash.noGaps': 'Not enough answers yet to show gaps.',
  'skills.dash.noAnswers': 'Nobody has answered yet.',
  'skills.dash.weekly': 'Answer pace',
  'skills.dash.weeklySub': 'Forms completed per week, last 12 weeks.',
  'skills.dash.week': 'Week of',
  'skills.dash.completed': 'Completed',
  'skills.dash.bonus': 'Plus (AI and more)',
  'skills.dash.bonusSub': 'Among the {count} people who finished the form.',
  'skills.dash.managers': 'Managers',
  'skills.dash.managersSub': 'Tap a name to see only their team.',
  'skills.dash.people': 'People',
  'skills.dash.empty': 'Nobody in this selection.',

  // --- Skill Matrix: simplified layout (2026-10) ---------------------------
  'skills.tab.people': 'People',
  'skills.tab.search': 'Find people',
  'skills.tab.config': 'Settings',
  'skills.tab.mine': 'My skills',
  'skills.moreInfo': 'More information',
  'skills.reviewAll': 'Go through it all',
  'skills.state.pending': 'Pending',
  'skills.state.progress': 'In progress',
  'skills.team.listTitle': 'Your people',
  'skills.config.roles': 'Roles and grids',
  'skills.config.catalog': 'Skills catalogue',
  'skills.search.addSkill': '+ Skill',
  'skills.search.available': 'No current project',
  'skills.search.minLevel': 'Minimum level for {skill}',
  'skills.search.removeSkill': 'Remove {skill}',
  'skills.over.coverage': 'Coverage by skill',
  'skills.over.coverageHint': 'The detail by category and role',
  'skills.over.upkeep': 'Keeping answers current',
  'skills.over.upkeepHint':
    'Asking to update shows the person the form again with their answers already selected: they only change what is different. Nobody loses what they answered.',
  'skills.over.stale': 'Old answers',
  'skills.over.byRole': 'A whole role',
  'skills.over.byRoleText': 'Useful after changing a role’s grid.',
  'skills.over.pickRole': 'Pick a role',
  'skills.over.everybody': 'Everybody',
  'skills.over.everybodyText': 'The yearly refresh for the whole office.',
  'skills.launch.title': 'Before launch',
  'skills.launch.pending.one': '{count} thing left',
  'skills.launch.pending.other': '{count} things left',
  'skills.launch.allSet': 'All set',
  'skills.launch.public': 'The module is open to the whole office',
  'skills.launch.access': '{done} of {total} people can get in',
  'skills.launch.accessHint':
    'Until the module is public, only people with access get in. Select people under People and use "Give access", or ask for the module to be opened to everybody (TALENT_MODULE_PUBLIC).',
  'skills.launch.alertsOn': 'Weekly reminders are on',
  'skills.launch.alertsOff': 'Weekly reminders are off',
  'skills.launch.alertsHint':
    'Without a reminder, only people who open the app on their own answer. They are switched on on the server (SKILL_MATRIX_ALERTS_ENABLED); launch day is the time.',
  'skills.launch.managers': '{done} of {total} people have a manager',
  'skills.launch.freelancers.one': '{count} freelancer without an email',
  'skills.launch.freelancers.other': '{count} freelancers without an email',
  'skills.launch.freelancersHint':
    'Without an email they cannot be sent the link. Add it on their profile, or download the links and send them yourself.',
  'skills.launch.emptyRoles': 'Roles with no questions: {list}',
  'skills.launch.rolesOk': 'Every role has questions',
  'skills.launch.goPeople': 'Go to People',
  'skills.launch.goConfig': 'Go to Settings',
  'skills.launch.srDone': '(done)',
  'skills.launch.srPending': '(pending)',
  'skills.dir.bulkRole': 'Add role…',
  'skills.dir.bulkRoleDone': 'Done: role added to {done} people ({skipped} already had it).',
  'skills.dir.bulkRemindDone': 'We sent {done} reminders. {skipped} were skipped (no email, already done or reminded recently).',
  'skills.dir.bulkAccess': 'Give access',
  'skills.dir.bulkAccessTitle': 'Lets them into the Skill Matrix before it is public',
  'skills.dir.bulkAccessDone': 'Done: {done} people can get in now. {skipped} have no linked email.',
  'skills.dir.bulkLinks': 'Download links',
  'skills.dir.bulkLinksTitle': 'A CSV with each person’s personal form link (replaces earlier ones)',
  'skills.dir.bulkLinksDone': 'We downloaded {done} links. Those people’s earlier links stopped working.',
  'skills.cat.title': 'Skills catalogue',
  'skills.cat.hint':
    'This is where names are fixed, unused skills retired and duplicates merged. Merging moves people’s levels to the one that stays (the higher wins) and retires the other; nobody loses an answer.',
  'skills.cat.search': 'Search skill or category…',
  'skills.cat.show.all': 'Active',
  'skills.cat.show.noGrid': 'In no grid',
  'skills.cat.show.unused': 'Unused',
  'skills.cat.show.inactive': 'Retired',
  'skills.cat.roles': 'Grids',
  'skills.cat.inNoGrid': 'In no grid',
  'skills.cat.none': 'No skills in this view.',
  'skills.cat.merge': 'Merge',
  'skills.cat.makeBonus': 'Make plus',
  'skills.cat.unBonus': 'Remove from plus',
  'skills.cat.retire': 'Retire',
  'skills.cat.restore': 'Restore',
  'skills.cat.shown.one': '{count} skill',
  'skills.cat.shown.other': '{count} skills',
  'skills.cat.editTitle': 'Edit skill',
  'skills.cat.name': 'Name',
  'skills.cat.editHint': 'The {count} people who rated it keep their level.',
  'skills.cat.mergeTitle': 'Merge “{name}”',
  'skills.cat.mergeInto': 'Move everything to',
  'skills.cat.mergeConfirm': 'Merge',
  'skills.cat.mergeExplain':
    'The {count} people who rated “{from}” move to “{into}” with their level (if they rated both, the higher stays). “{from}” is retired.',
  'skills.rowMenu': 'Actions for {name}',
  'skills.dir.moreFilters': 'Filters',
  'quick.title': 'Smart Booking',
  'quick.headline': '{date} at {station}',
  'quick.lunch': 'Lunch {slot}',
  'quick.slot.12md-1pm': '12 pm to 1 pm',
  'quick.slot.1pm-2pm': '1 pm to 2 pm',
  'quick.glutenFree': 'Gluten-free',
  'quick.noLunch': 'No lunch',
  'quick.lunchReason.FRIDAY': 'No lunch: there is no lunch service on Fridays',
  'quick.lunchReason.DAY_DISABLED': 'No lunch: there is no lunch service that day',
  'quick.lunchReason.CUTOFF': 'No lunch: orders for that day are closed',
  'quick.lunchReason.FULL': 'No lunch: no seats left',
  'quick.parking': 'With parking',
  'quick.noParking': 'No parking',
  'quick.usualDay.1': 'You usually come in on Mondays',
  'quick.usualDay.2': 'You usually come in on Tuesdays',
  'quick.usualDay.3': 'You usually come in on Wednesdays',
  'quick.usualDay.4': 'You usually come in on Thursdays',
  'quick.usualDay.5': 'You usually come in on Fridays',
  'quick.dismiss': 'Hide Smart Booking',
  'quick.success': 'Done: you booked {station} for {date}.',
  'quick.taken': 'Someone took that spot a moment ago. We found you another one.',
  'quick.failed': 'Smart Booking could not make the booking. Try from the map.',
  'booking.prefilled': 'Filled in from your recent bookings. You can change it.',
  'map.legend.presentIn': 'In the office',
  'map.legend.presentExpected': 'Not here yet',
  'map.presence.in': 'In the office',
  'map.presence.expected': 'Not here yet',
  'booking.stationAria.in': 'Workstation {station}, taken; the person is already in the office',
  'booking.stationAria.expected': 'Workstation {station}, taken; the person has not arrived yet',
  'equipment.listLabel': 'Equipment at this workstation',
  'equipment.report': 'Report a problem',
  'equipment.reportTitle': 'Problem at workstation {station}',
  'equipment.whichOne': 'Which equipment?',
  'equipment.general': 'The workstation in general',
  'equipment.whatHappens': 'What is wrong?',
  'equipment.placeholder': 'For example: the monitor is missing, the dock does not charge',
  'equipment.send': 'Send report',
  'equipment.reportSent': 'Thanks. The asset team got your report.',
  'equipment.reportFailed': 'The report could not be sent. Try again in a moment.',
  'skills.search.inOffice': 'In the office now',
  'skills.search.inOfficeNow': 'In the office',
  'skills.search.inOfficeAt': 'In the office · {desk}',
  // --- Assets: inventory audit (barcode scan) ----------------------------
  'assets.audit.nav': 'Audit',
  'assets.audit.title': 'Inventory audits',
  'assets.audit.subtitle': 'Walk a location scanning the plates and compare what you find with the record.',
  'assets.audit.new': 'New audit',
  'assets.audit.empty': 'No audits yet.',
  'assets.audit.loadError': 'Could not load the audits: {message}',
  'assets.audit.loadOneError': 'Could not load the audit: {message}',
  'assets.audit.notFound': 'Audit not found.',
  'assets.audit.wholeOffice': 'Whole office',
  'assets.audit.status.open': 'Open',
  'assets.audit.status.closed': 'Closed',
  'assets.audit.col.started': 'Started',
  'assets.audit.col.location': 'Location',
  'assets.audit.col.status': 'Status',
  'assets.audit.col.found': 'Found',
  'assets.audit.col.missing': 'Missing',
  'assets.audit.col.startedBy': 'Started by',
  'assets.audit.foundOf': '{found} of {expected}',
  'assets.audit.newBody': 'Choose what you will walk through. The whole office expects every office asset except the ones out for maintenance.',
  'assets.audit.newLocation': 'Location to audit',
  'assets.audit.newNote': 'Note (optional)',
  'assets.audit.start': 'Start',
  'assets.audit.back': 'Audits',
  'assets.audit.sessionTitle': 'Audit: {location}',
  'assets.audit.startedMeta': 'Started on {date} by {email}',
  'assets.audit.closedMeta': 'Closed on {date} by {email}',
  'assets.audit.scanLabel': 'Scan or type the plate and press Enter',
  'assets.audit.scanPlaceholder': 'Asset plate',
  'assets.audit.viewerHint': 'Only the asset administrator can scan in this audit.',
  'assets.audit.camera': 'Scan with camera',
  'assets.audit.cameraHint': 'Point at the plate barcode. Each code read is recorded on its own.',
  'assets.audit.cameraError': 'Could not open the camera: {message}',
  'assets.audit.cameraLast': 'Last code read: {code}',
  'assets.audit.cameraDone': 'Done',
  'assets.audit.counter.found': 'Found',
  'assets.audit.counter.misplaced': 'Elsewhere',
  'assets.audit.counter.unknown': 'Unknown',
  'assets.audit.counter.disposed': 'Written off',
  'assets.audit.counter.missing': 'Missing',
  'assets.audit.counter.expected': 'Expected',
  'assets.audit.counter.duplicate': 'Repeats',
  'assets.audit.counters': 'Audit summary',
  'assets.audit.result.found': 'Found',
  'assets.audit.result.misplaced': 'Elsewhere',
  'assets.audit.result.unknown': 'Unknown',
  'assets.audit.result.disposed': 'Written off',
  'assets.audit.result.duplicate': 'Repeat',
  'assets.audit.result.pending': 'Processing',
  'assets.audit.result.failed': 'Not recorded',
  'assets.audit.result.moved': 'Moved here',
  'assets.audit.recordedAt': 'Recorded at {place}',
  'assets.audit.atHome': 'the employee’s home',
  'assets.audit.noAsset': 'No plate matches this code.',
  'assets.audit.move': 'Move to this location',
  'assets.audit.moveTitle': 'Move asset',
  'assets.audit.moveBody': 'Move {plate} from {from} to {to}? The change is kept in the asset history.',
  'assets.audit.moveConfirm': 'Move',
  'assets.audit.moved': '{plate} is now recorded at {to}.',
  'assets.audit.scansTitle': 'Scans',
  'assets.audit.scansEmpty': 'Nothing scanned yet.',
  'assets.audit.scanError': '{code} was not recorded: {message}',
  'assets.audit.missingTitle': 'Missing assets',
  'assets.audit.missingHint': 'Recorded here and not scanned yet.',
  'assets.audit.missingHintClosed': 'Recorded here and never scanned during the audit.',
  'assets.audit.missingEmpty': 'No asset is missing.',
  'assets.audit.close': 'Close audit',
  'assets.audit.closeBody.one': 'Once closed it stays as a record and accepts no more scans. {count} asset will be recorded as missing.',
  'assets.audit.closeBody.other': 'Once closed it stays as a record and accepts no more scans. {count} assets will be recorded as missing.',
  'assets.audit.closedBanner': 'Audit closed: this is the final report.',
  'assets.audit.csv.type': 'Type',
  'assets.audit.csv.scan': 'Scan',
  'assets.audit.csv.missing': 'Missing',
  'assets.audit.csv.code': 'Scanned code',
  'assets.audit.csv.plate': 'Plate',
  'assets.audit.csv.result': 'Result',
  'assets.audit.csv.category': 'Category',
  'assets.audit.csv.brand': 'Brand',
  'assets.audit.csv.model': 'Model',
  'assets.audit.csv.recorded': 'Recorded location',
  'assets.audit.csv.by': 'Scanned by',
  'assets.audit.csv.at': 'Date',
  'assets.audit.csv.filename': 'audit-{id}-{date}.csv',
  'assets.issues.nav': 'Workstation reports',
  'assets.issues.title': 'Workstation reports',
  'assets.issues.subtitle': 'Problems reported by whoever had the workstation booked. Each one also reaches the asset team by mail.',
  'assets.issues.filter': 'Show reports',
  'assets.issues.open': 'Open',
  'assets.issues.resolved': 'Resolved',
  'assets.issues.emptyOpen': 'No open reports.',
  'assets.issues.emptyResolved': 'No resolved reports yet.',
  'assets.issues.loadError': 'The reports could not be loaded.',
  'assets.issues.general': 'The workstation in general',
  'assets.issues.reportedBy': 'Reported by {name}',
  'assets.issues.resolvedBy': 'Resolved by {name} on {date}',
  'assets.issues.resolve': 'Mark as resolved',
  'reports.desk.smartBooking': 'With Smart Booking',
  'reports.desk.smartBookingDesc': '{pct}% of bookings since it is measured · {people} people',
  'reports.desk.smartBookingSince': 'Measured since October 22, 2026',
  'quick.thinking': 'Looking at your recent bookings…',
  'quick.why.FAVORITE': 'Your favorite workstation is free that day.',
  'quick.why.BESIDE_FAVORITE': '{favorite} is taken that day, so here is {station}, right next to it.',
  'quick.why.FACING_FAVORITE': '{favorite} is taken that day; {station} is right across from it.',
  'quick.why.OTHER_FAVORITE': '{favorite} is taken that day; {station} is another one you use often.',
  'quick.why.NEAR_OTHER_FAVORITE': '{favorite} is taken that day; {station} is close to the ones you usually pick.',
  'quick.setup.same.DUAL_MONITOR': 'It also has two monitors.',
  'quick.setup.same.MONITOR': 'It has one monitor, like yours.',
  'quick.setup.same.HIGH_DESK': 'It is also a high table.',
  'quick.setup.other.DUAL_MONITOR': 'And it has two monitors.',
  'quick.setup.other.MONITOR': 'Heads up: it has one monitor.',
  'quick.setup.other.HIGH_DESK': 'Heads up: it is a high table, with no monitor.',
  'quick.otherOptions': 'Other options:',
  'quick.alt.FAVORITE': 'your favorite',
  'quick.alt.BESIDE_FAVORITE': 'next to it',
  'quick.alt.FACING_FAVORITE': 'across',
  'quick.alt.OTHER_FAVORITE': 'frequent',
  'quick.alt.NEAR_OTHER_FAVORITE': 'nearby',
  'quick.pickAria': 'Pick {station}, {where}',
  'quick.basedOn.one': 'Based on your last booking',
  'quick.basedOn.other': 'Based on your last {count} bookings',
  'extraLunch.selfAssigned': 'Self-assigned via chat',
  'assistant.more': 'More ({count})',
  'assistant.adminLabel': 'Admins only',
  'assistant.qrEnlarge': 'Enlarge the QR',
  'assistant.languageToggle': 'Chat language',
  'assistantIntro.title': 'New: your booking assistant',
  'assistantIntro.subtitle': "It's the robot in the bottom-left corner. Write to it the way you'd talk to a coworker, or tap an option, and get this done without going through the screens:",
  'assistantIntro.book.title': 'Book by talking',
  'assistantIntro.book.desc': 'Desk, lunch and parking in one sentence.',
  'assistantIntro.book.example': '"Book me Thursday with lunch at 1"',
  'assistantIntro.week.title': 'Book your week',
  'assistantIntro.week.desc': 'Your usual days, this week or next, with your desk, lunch and parking. It shows you the plan and you confirm.',
  'assistantIntro.week.example': '"Book my week"',
  'assistantIntro.waitlist.title': 'Automatic waitlist',
  'assistantIntro.waitlist.desc': 'On a full day, it books you as soon as someone cancels and tells you how many are ahead.',
  'assistantIntro.waitlist.example': '"Put me on the waitlist for Friday"',
  'assistantIntro.qr.title': 'Your lunch QR',
  'assistantIntro.qr.desc': 'See it and enlarge it in the chat.',
  'assistantIntro.qr.example': '"Show me my QR"',
  'assistantIntro.leftovers.title': 'Leftover lunches',
  'assistantIntro.leftovers.desc': 'If someone cancelled late, assign one to yourself.',
  'assistantIntro.leftovers.example': '"Any leftover lunches?"',
  'assistantIntro.seat.title': 'Change seat',
  'assistantIntro.seat.desc': 'It only offers free desks, by row.',
  'assistantIntro.seat.example': '"Move me to row C"',
  'assistantIntro.attendance.title': 'Your attendance',
  'assistantIntro.attendance.desc': 'What time you arrived and how many days you came in.',
  'assistantIntro.attendance.example': '"How many days did I come in this month?"',
  'assistantIntro.language.title': 'English or Spanish',
  'assistantIntro.language.desc': 'Write in either, or switch it at the top with ES | EN.',
  'assistantIntro.language.example': '"Háblame en español"',
  'assistantIntro.admin.title': 'For admins',
  'assistantIntro.admin.desc': 'Who is coming, lunches per day and per month, the late cancellations report by email, book or cancel for others. Their options show in gold.',
  'assistantIntro.admin.example': '"Who is coming tomorrow?"',
  'assistantIntro.later': 'Later',
  'assistantIntro.try': 'Try the chat',
  'assistant.open': 'Open the booking assistant',
  'assistant.close': 'Close the assistant',
  'assistant.newChat': 'New conversation',
  'assistant.title': 'Booking assistant',
  'assistant.subtitle': 'Desks, lunch and more',
  'assistant.welcome':
    "Hi, I'm your booking assistant. I can book you a desk, cancel or change your reservations, adjust your lunch, tell you about leftover lunches and look up when you came to the office. Just write it however you like.",
  'assistant.suggest.bookTomorrow': 'Book me tomorrow',
  'assistant.suggest.myReservations': 'What reservations do I have?',
  'assistant.suggest.leftovers': 'Any leftover lunches today?',
  'assistant.aiBadge': 'New',
  'assistant.greeting': 'Want help booking?',
  'assistant.footnote': 'Tap an option or write it however you like. Changes are confirmed by email.',
  'assistant.suggest.attendance': 'How many days did I come in this month?',
  'assistant.placeholder': 'Type your message',
  'assistant.send': 'Send',
  'assistant.you': 'You',
  'assistant.thinking': 'The assistant is typing',
  'assistant.error': 'I could not reach the assistant. Please try again in a moment.',
};
